import { Controller } from "@hotwired/stimulus"
import Konva from "konva"
import { loadFont, textToPath } from "../lib/text_to_path"
import { shapePoints, pointsToPathD } from "../lib/shape_points"
import { clamp, pxToMm, mmToPx } from "../lib/geom"
import { zoomToPoint, clampPan } from "../lib/viewport"
import { moveItem } from "../lib/layers"
import { replacedImageGeom } from "../lib/image_replace"
import { svgPhysicalSizeMm } from "../lib/svg_size"
import { rasterDpi, physicalMmFromPx, DEFAULT_IMPORT_DPI } from "../lib/image_dpi"
import { safeMarginBandPx } from "../lib/safe_margin"
import { saveErrorMessage } from "../lib/save_error"
import { dedupeImageData } from "../lib/dedupe_images"
import { PRINT_DPI, resampleScale, resampledSize } from "../lib/image_resample"
import { continuationLayerData } from "../lib/continuation"
import { transferToPieceCenter } from "../lib/seam_continuation"
import { resolveText, totalScale, isOverflow } from "../lib/text_fit"
import { capRatio, digitRatio, capToEm, emToCap, DIGIT_MISMATCH } from "../lib/cap_height.js"

// Дефолтный физический размер нового слоя: длинная сторона в мм.
// Абсолютный (не доля детали) → один и тот же размер на полочке/спинке/рукаве.
const DEFAULT_LAYER_MM = 100


// Зеркало Font.style_key (Ruby): выбор начертания по флагам bold/italic.
// Клиент и сервер ОБЯЗАНЫ выбирать одно и то же начертание.
function styleKey(cfg) {
  const b = !!cfg.bold, i = !!cfg.italic
  return b && i ? "bold_italic" : b ? "bold" : i ? "italic" : "regular"
}

// Редактор дизайнера: все детали футболки на одном экране, у каждой — свой холст.
// Деталь вписана целиком, слои (бекграунд + логотипы) грузятся одной кнопкой и
// обрезаются по контуру именно этой детали. Масштаб/поворот — ручками Transformer
// или кнопками (на случай, когда ручки уехали за край).
export default class extends Controller {
  static targets = ["canvas", "status", "gridBtn", "gridVal", "saveBtn", "modal", "modalTitle", "loading", "loadingText", "rulerBtn", "sizeW", "sizeH", "posX", "posY", "lockBtn", "overlay", "overlaySlot", "overlayBar", "undoBtn", "main", "aside", "panelExpanded", "panelCollapsed", "panelEmpty", "panelImage", "panelText", "imageName", "imageThumb", "rotInput", "flipBtn", "fontBtn", "fontMenu", "dirtyDot", "marginBtn", "recipientSelect", "recipientBar", "recipientBarText", "panelOverride", "overrideScaleLabel", "overflowBadge"]
  static values = { url: String, template: String, designId: Number, orderId: Number, productId: Number, title: String, seamAllowance: Number, recipientsUrl: String, overridesUrl: String, designSize: String, previewRecipientId: String }

  async connect() {
    this.editors = new Map()
    this.gridOn = false
    this.rulerOn = false
    this.ratioLocked = true
    this.designTitle = this.titleValue || ""
    this._lastEditor = null   // последняя правленая деталь — цель Ctrl+Z, когда ничего не выделено

    this.keyHandler = (e) => {
      if (e.code === "Space" && !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName)) {
        e.preventDefault()   // на КАЖДЫЙ keydown, включая автоповтор при удержании — иначе страница скроллится пробелом
        if (!this._panning) { this._panning = true; for (const ed of this.editors.values()) ed.setPanMode(true) }
        return
      }
      // Esc — выйти из полноэкранного режима детали
      if (e.key === "Escape" && this._maxEditor) { e.preventDefault(); this.closeMaximize(); return }
      // фокус в поле ввода (название в модалке, размеры, селект) — клавиши ему, не слою
      const el = document.activeElement
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" ||
                 el.tagName === "SELECT" || el.isContentEditable)) return

      // Ctrl/Cmd+Z — отмена на активной (или последней правленой) детали; работает без выделения.
      // e.code === "KeyZ" — физическая клавиша, не зависит от раскладки (рус. «я» тоже сработает).
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.code === "KeyZ") {
        e.preventDefault()
        this.undo()
        return
      }

      if (!this.activeEditor) return

      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault()
        this.activeEditor.deleteSelected()
        return
      }

      // стрелки — перемещение выбранного слоя.
      // Без сетки: 1 мм, с Shift — 10 мм. С сеткой: прыжок на 1 клетку, с Shift — на 5.
      const step = e.shiftKey ? 10 : 1
      const cells = e.shiftKey ? 5 : 1
      const moves = {
        ArrowUp: [0, -step], ArrowDown: [0, step],
        ArrowLeft: [-step, 0], ArrowRight: [step, 0],
      }
      const m = moves[e.key]
      if (m) {
        e.preventDefault()
        this.activeEditor.nudge(m[0], m[1], cells)
      }
    }
    window.addEventListener("keydown", this.keyHandler)
    this.keyUpHandler = (e) => {
      if (e.code === "Space" && this._panning) { this._panning = false; for (const ed of this.editors.values()) ed.setPanMode(false) }
    }
    window.addEventListener("keyup", this.keyUpHandler)

    this._ensureDbgUI()   // кнопка «DBG» доступна сразу, не ждём первого _dbg

    try {
      this.fonts = await fetch("/fonts.json").then((r) => r.ok ? r.json() : []).catch(() => [])
      this.fontById = new Map(this.fonts.map((f) => [f.id, f]))
      this.loadedFonts = new Map()   // "fontId:style" → opentype.Font (лениво, через loadFontStyle)
      const pieces = await this.loadContours()
      this.pieces = pieces
      // карточка фитится по высоте видимой области main; холст берёт остаток через flex
      this._setCardHeight()
      const { w, h, scale } = this._measureShared()
      // запоминаем для возврата из полноэкранного режима
      this.cardW = w; this.cardH = h; this.sharedScale = scale
      for (const canvas of this.canvasTargets) {
        const piece = pieces.get(canvas.dataset.piece)
        if (!piece) continue
        this.editors.set(piece.id, new PieceEditor(canvas, piece, w, h, {
          scale,
          editorCtl: this,
          onSelect: (ed, node) => {
            // выделение эксклюзивно: выбор слоя на одной детали снимает его на остальных
            if (node) for (const other of this.editors.values()) { if (other !== ed && other.selected) other.select(null) }
            this.activeEditor = node ? ed : null
            this.refreshPanel(ed, node)
            this._refreshUndoBtn()
          },
          onHistory: (ed) => { this._lastEditor = ed; this._refreshUndoBtn(); this._setDirty(true) },
        }))
      }
      this.setStatus("")
      console.log("[editor] connect", { designId: this.designIdValue, template: this.templateValue, pieces: [...this.editors.keys()] })
      if (this.designIdValue > 0) await this.loadDesign()
      await this._loadRecipients()
      this._refreshUndoBtn()
      this._observeResize()
      this._setDirty(false)   // загрузка/восстановление — не «правки»
      // уход со страницы с несохранёнными правками — предупредить (закрытие вкладки и turbo-переходы)
      this._beforeUnload = (e) => { if (this._dirty) { e.preventDefault(); e.returnValue = "" } }
      window.addEventListener("beforeunload", this._beforeUnload)
      this._beforeVisit = (e) => {
        if (this._dirty && !confirm("Есть несохранённые изменения — уйти без сохранения?")) e.preventDefault()
      }
      document.addEventListener("turbo:before-visit", this._beforeVisit)
    } catch (e) {
      this.setStatus(`Ошибка загрузки контуров: ${e.message}`)
      console.error("[editor] connect failed", e)
      throw e
    }
  }

  async loadDesign() {
    try {
      console.log("[editor] loadDesign fetch", this.designIdValue)
      const res = await fetch(`/designs/${this.designIdValue}.json`)
      console.log("[editor] loadDesign response", res.status)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      console.log("[editor] loadDesign data", { title: data.title, layers: data.layers.length })
      this.designTitle = data.title || ""

      // 1) сначала грузим все картинки (детерминированно), 2) добавляем строго по z_index
      const total = data.layers.filter((l) => l.kind === "image" && l.image_url).length
      let loaded = 0
      this.showLoading(total)
      const prepared = await Promise.all(
        data.layers.map(async (l) => {
          if (l.kind === "image" && l.image_url) {
            const img = await loadImage(l.image_url).catch((e) => {
              console.error("[editor] image load failed", l.piece, e)
              return null
            })
            this.updateLoading(++loaded, total)
            return { l, img }
          }
          return { l, img: null }
        })
      )
      this.hideLoading()
      prepared.sort((a, b) => (a.l.z_index ?? 0) - (b.l.z_index ?? 0))
      // шрифты для всех text-слоёв должны быть в loadedFonts до построения узлов
      const texts = prepared.filter(({ l }) => l.kind === "text")
      if (texts.length) {
        await this.ensureFontsLoaded()
        await Promise.all(texts.map(({ l }) => this.ensureStyleForCfg(l.config || {})))
      }
      for (const { l, img } of prepared) {
        const ed = this.editors.get(l.piece)
        if (!ed) { console.warn("[editor] no editor for piece", l.piece); continue }
        if (l.kind === "fill") {
          ed.setFill(l.color)
          console.log("[editor] restored fill", l.piece, l.color)
        } else if (l.kind === "image" && img) {
          ed.addImageData(img, l)
          console.log("[editor] restored image", l.piece, l.z_index)
        } else if (l.kind === "text") {
          ed.addExistingText(l)
          console.log("[editor] restored text", l.piece, l.z_index)
        }
      }
      this.setStatus("Макет загружен.")
      for (const ed of this.editors.values()) ed.renderLayers()
    } catch (e) {
      this.setStatus(`Ошибка загрузки макета: ${e.message}`)
      console.error("[editor] loadDesign failed", e)
    } finally {
      this.hideLoading()
    }
  }

  // --- Текстовые слои ---

  // Загрузить opentype.Font нужного стиля (ключ "id:style"); fallback URL → regular.
  async loadFontStyle(fontId, style) {
    const f = this.fontById.get(fontId)
    if (!f || !f.files) return null
    const url = f.files[style] || f.files.regular
    if (!url) return null
    const key = `${fontId}:${style}`
    // кэшируем промис (не результат): параллельные вызовы не дублируют fetch одного шрифта
    if (!this._fontLoads) this._fontLoads = new Map()
    if (!this._fontLoads.has(key)) {
      this._fontLoads.set(key, loadFont(url).then(
        (font) => { this.loadedFonts.set(key, font); return font },
        (e) => { console.error("[editor] font style load failed", key, e); this._fontLoads.delete(key); return null }
      ))
    }
    return this._fontLoads.get(key)
  }

  // Загрузить regular всех шрифтов (для нового текста); вызывается перед addText.
  async ensureFontsLoaded() {
    if (!this.fonts || !this.fonts.length) return
    await Promise.all(this.fonts.map((f) => this.loadFontStyle(f.id, "regular")))
  }

  // Гарантировать загрузку стиля под cfg выделенного/восстановленного слоя.
  async ensureStyleForCfg(cfg) {
    if (cfg && cfg.font_id != null) await this.loadFontStyle(Number(cfg.font_id), styleKey(cfg))
  }

  // Кнопка «+ Текст»: грузим шрифты, добавляем дефолтный слой на выбранную деталь
  async addText(event) {
    const ed = this.editorFor(event)
    if (!ed) return
    if (!this.fonts || !this.fonts.length) {
      this.setStatus("Нет загруженных шрифтов — добавьте шрифт на /fonts")
      return
    }
    try {
      this.setStatus("Загрузка шрифтов…")
      await this.ensureFontsLoaded()
      const first = this.fonts[0]
      const cfg = {
        content: "Текст", font_id: first?.id, font_size_mm: 50,
        color: "#000000", align: "center", letter_spacing: 0,
        bold: false, italic: false,
        stroke: { color: "#ffffff", width: 0 },
        inner_stroke: { color: "#ffffff", width: 0 },
        shadow: { color: "#000000", dx: 0, dy: 0 },
        personalization: { enabled: false, field: "name", sample: "ИВАНОВ" },
        max_width_mm: 0,   // 0 = без предела; длинные фамилии ужимаются под него (TextFit)
      }
      ed.addText(cfg)
      this.setStatus("Текстовый слой добавлен.")
    } catch (e) {
      console.error("[editor] addText failed", e)
      this.setStatus(`Не удалось добавить текст: ${e.message}`)
    }
  }

  // Применить изменение свойства к выделённому text-слою и перестроить его
  async updateTextProp(event) {
    const ed = this.activeEditor
    const node = ed?.selected
    if (!node || !ed.isTextNode(node)) return
    const el = event.target
    const prop = el.dataset.prop
    if (!prop) return
    let value
    if (el.type === "checkbox") value = el.checked
    else if (el.type === "number" || el.type === "range") value = parseFloat(el.value) || 0
    else if (prop === "font_id") value = parseInt(el.value, 10)   // селект отдаёт строку, loadedFonts по числу
    else value = el.value

    // hex-поле цвета: применяем только валидный #rrggbb (пока печатают — ждём)
    if (el.type === "text" && /(^|\.)color$/.test(prop)) {
      value = value.trim()
      if (!/^#[0-9a-f]{6}$/i.test(value)) return
    }

    const cfg = JSON.parse(JSON.stringify(node.getAttr("config") || {}))
    // Кегль узла ДО общего присваивания ниже — на случай, если в cap-режиме
    // окажется, что метрику шрифта взять неоткуда (см. комментарий у guard'а).
    const prevFontSizeMm = cfg.font_size_mm
    const path = prop.split(".")   // напр. "stroke.width", "personalization.enabled"
    if (path.length === 1) {
      cfg[path[0]] = value
    } else {
      cfg[path[0]] = cfg[path[0]] || {}
      cfg[path[0]][path[1]] = value
    }
    // В config всегда уезжает кегль. Введённое в режиме "высота букв" пересчитываем
    // здесь — дальше по коду (replaceTextNode, exportLayers, сервер) о единице никто не знает.
    if (this._sizeUnitOf(cfg) === "cap") {
      if (prop === "font_size_mm") {
        cfg.cap_height_mm = value
      }
      // Смена шрифта или Ж/К меняет cap-ratio: чтобы физическая высота осталась
      // прежней, кегль обязан поехать следом. Начертание нужно догрузить ДО замера,
      // иначе _capRatioFor вернёт метрику старого файла.
      if (prop === "font_id" || prop === "bold" || prop === "italic") {
        await this.ensureStyleForCfg(cfg)
      }
      const font = this._fontForCfg(cfg)
      const target = cfg.cap_height_mm
      // Без метрики шрифта пересчитывать нечем: capRatio(null) отдал бы запасные
      // 0.7, и кегль уехал бы на сервер, где шрифт есть и печатается настоящей
      // метрикой. Если правили именно поле "Размер", присваивание выше уже
      // записало в font_size_mm сырую введённую ВЫСОТУ (а не кегль) — откатываем
      // на прежний кегль узла, а не оставляем выдуманное значение.
      if (font && target > 0) cfg.font_size_mm = Math.max(1, capToEm(target, capRatio(font)))
      else if (prop === "font_size_mm") cfg.font_size_mm = prevFontSizeMm
    }
    // догрузить нужное начертание (Ж/К/смена шрифта) перед перестроением
    await this.ensureStyleForCfg(cfg)
    ed.replaceTextNode(node, cfg)

    // зеркала того же свойства (свотч+hex, скрытый select) — синхронизируем, кроме активного поля
    if (this.hasPanelTextTarget) {
      for (const twin of this.panelTextTarget.querySelectorAll(`[data-prop="${prop}"]`)) {
        if (twin === el || document.activeElement === twin) continue
        if (twin.type === "checkbox") twin.checked = !!value
        else twin.value = value ?? ""
      }
      this._syncSwatch(prop, value)
      if (prop === "font_id") this._refreshFontBtn(value)
      if (prop === "align") this._refreshAlignButtons(value)
      if (prop === "personalization.enabled")
        this.panelTextTarget.querySelector("[data-pers-fields]")?.classList.toggle("hidden", !value)
    }
    // предел ширины или сам текст поменялись → пересчитать «не влезло»
    this._refreshOverflowBadge()
    this._refreshSizeHint(cfg)
  }

  // Свотч цвета рядом с hex-полем
  _syncSwatch(prop, val) {
    if (!this.hasPanelTextTarget) return
    const sw = this.panelTextTarget.querySelector(`[data-swatch-for="${prop}"]`)
    if (sw) sw.style.background = val || "#ffffff"
  }

  // opentype.Font активного начертания слоя. null, если шрифт ещё не догружен —
  // capRatio на null отдаёт FALLBACK_RATIO, поле не показывает NaN.
  _fontForCfg(cfg) {
    const fid = Number(cfg?.font_id)
    return this.loadedFonts?.get(`${fid}:${styleKey(cfg)}`)
        || this.loadedFonts?.get(`${fid}:regular`)
        || null
  }

  _capRatioFor(cfg) { return capRatio(this._fontForCfg(cfg)) }

  // Единица ввода: "cap" — оператор задаёт высоту букв, "em" (умолчание) — кегль.
  // Умолчание критично: у всех уже сохранённых слоёв ключа нет.
  _sizeUnitOf(cfg) { return cfg?.size_unit === "cap" ? "cap" : "em" }

  // Переключение селекта единиц: значение на канвасе НЕ меняется, меняется только
  // то, что показано в поле. font_size_mm остаётся прежним.
  changeSizeUnit(event) {
    const ed = this.activeEditor
    const node = ed?.selected
    if (!node || !ed.isTextNode(node)) return
    const unit = event.target.value === "cap" ? "cap" : "em"
    const cfg = JSON.parse(JSON.stringify(node.getAttr("config") || {}))
    cfg.size_unit = unit
    if (unit === "cap") cfg.cap_height_mm = emToCap(cfg.font_size_mm || 50, this._capRatioFor(cfg))
    else delete cfg.cap_height_mm
    node.setAttr("config", cfg)
    this.refreshTextPanel(ed, node)
    // Не save(): это полное сохранение (PATCH всех слоёв / POST создания макета с
    // переходом по edit_url на несохранённом макете) — селект единиц не должен
    // его триггерить, как и любой другой проп в updateTextProp. Правка меняет
    // config, поэтому просто помечаем макет несохранённым, как везде в файле.
    this._setDirty(true)
  }

  // Подсказка о расхождении заданной высоты с фактической. Две независимые
  // причины, могут сработать обе сразу:
  //   1) TextFit ужал надпись под max_width_mm (k < 1);
  //   2) у шрифта old-style цифры — они ниже заглавных (Alegreya Sans, Yanone).
  _refreshSizeHint(cfg) {
    if (!this.hasPanelTextTarget) return
    const el = this.panelTextTarget.querySelector("[data-size-hint]")
    if (!el) return
    const lines = []
    if (this._sizeUnitOf(cfg) === "cap") {
      const cr = this._capRatioFor(cfg)
      const dr = digitRatio(this._fontForCfg(cfg))
      const capMm = emToCap(cfg.font_size_mm || 0, cr)
      const k = this._fitScaleFor()
      if (k < 0.999) lines.push(`после автоподгонки ≈ ${Math.round(capMm * k)} мм`)
      if (cr > 0 && Math.abs(dr - cr) / cr > DIGIT_MISMATCH) {
        lines.push(`цифры у этого шрифта ниже: ≈ ${Math.round((cfg.font_size_mm || 0) * dr * k)} мм`)
      }
    }
    el.textContent = lines.join(" · ")
    el.classList.toggle("hidden", lines.length === 0)
  }

  // Множитель TextFit для текущего слоя. Ширину берём из уже построенной
  // Konva-группы (textInner несёт ровно этот scale) — не векторизуем заново.
  _fitScaleFor() {
    const node = this.activeEditor?.selected
    const inner = node?.findOne?.(".textInner")
    const k = inner?.scaleX?.()
    return Number.isFinite(k) && k > 0 ? k : 1
  }

  // Сегмент выравнивания Л/Ц/П: активная кнопка — синяя
  _refreshAlignButtons(align) {
    if (!this.hasPanelTextTarget) return
    for (const btn of this.panelTextTarget.querySelectorAll("[data-align]")) {
      const on = btn.dataset.align === align
      btn.classList.toggle("bg-brand-blue", on)
      btn.classList.toggle("text-white", on)
      btn.classList.toggle("text-brand-graphite", !on)
    }
  }

  // Клик по кнопке сегмента — через скрытый select, чтобы идти общим путём updateTextProp
  pickAlign(event) {
    const sel = this.panelTextTarget.querySelector('[data-prop="align"]')
    if (!sel) return
    sel.value = event.currentTarget.dataset.align
    sel.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // --- Шрифт: кастомный дропдаун, пункты отрисованы своим шрифтом ---

  // CSS-шрифт для превью: FontFace из того же URL, что грузит opentype (браузер берёт из кэша)
  _ensureCssFont(f) {
    if (!f?.files?.regular) return
    if (!this._cssFonts) this._cssFonts = new Set()
    if (this._cssFonts.has(f.id)) return
    this._cssFonts.add(f.id)
    const face = new FontFace(`editor-font-${f.id}`, `url("${f.files.regular}")`)
    face.load().then((ff) => document.fonts.add(ff)).catch((e) => console.warn("[editor] css font preview failed", f.id, e))
  }

  _refreshFontBtn(fontId) {
    if (!this.hasFontBtnTarget) return
    const f = this.fontById.get(Number(fontId))
    const label = this.fontBtnTarget.querySelector("[data-font-btn-label]")
    if (label) {
      label.textContent = f?.name || "—"
      if (f) { this._ensureCssFont(f); label.style.fontFamily = `"editor-font-${f.id}", Inter, sans-serif` }
    }
  }

  toggleFontMenu() {
    if (!this.hasFontMenuTarget) return
    if (!this.fontMenuTarget.classList.contains("hidden")) { this.closeFontMenu(); return }
    // строим пункты лениво, шрифты превью грузим при первом открытии
    if (!this.fontMenuTarget.dataset.filled) {
      for (const f of this.fonts) {
        this._ensureCssFont(f)
        const item = document.createElement("button")
        item.type = "button"
        item.className = "block w-full truncate rounded-lg px-3 py-2 text-left text-[15px] text-brand-ink transition-colors hover:bg-brand-blue-soft"
        item.style.fontFamily = `"editor-font-${f.id}", Inter, sans-serif`
        item.textContent = f.name
        item.dataset.fontId = f.id
        item.addEventListener("click", () => this.pickFont(f.id))
        this.fontMenuTarget.appendChild(item)
      }
      this.fontMenuTarget.dataset.filled = "1"
    }
    this.fontMenuTarget.classList.remove("hidden")
    // закрытие кликом вне меню
    this._fontMenuAway = (e) => {
      if (!this.fontMenuTarget.contains(e.target) && !this.fontBtnTarget.contains(e.target)) this.closeFontMenu()
    }
    setTimeout(() => document.addEventListener("click", this._fontMenuAway), 0)
  }

  closeFontMenu() {
    if (!this.hasFontMenuTarget) return
    this.fontMenuTarget.classList.add("hidden")
    if (this._fontMenuAway) { document.removeEventListener("click", this._fontMenuAway); this._fontMenuAway = null }
  }

  pickFont(fontId) {
    this.closeFontMenu()
    const sel = this.panelTextTarget.querySelector('[data-prop="font_id"]')
    if (!sel) return
    sel.value = String(fontId)
    sel.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Правая панель: переключение состояния (пусто / картинка / текст) + заполнение полей
  refreshPanel(ed, node) {
    if (!this.hasPanelEmptyTarget) return
    const isText = !!(ed && node && ed.isTextNode(node))
    const isImage = !!(ed && node && node.className === "Image")
    this.panelEmptyTarget.classList.toggle("hidden", isText || isImage)
    this.panelImageTarget.classList.toggle("hidden", !isImage)
    this.panelTextTarget.classList.toggle("hidden", !isText)
    this.refreshSize(ed, node)
    this.refreshPos(ed, node)
    if (isImage) {
      if (this.hasImageNameTarget && document.activeElement !== this.imageNameTarget)
        this.imageNameTarget.value = node.getAttr("layerName") || ""
      // миниатюра слоя в плитке хедера (fallback — иконка из разметки)
      if (this.hasImageThumbTarget && node.image()?.src) {
        let img = this.imageThumbTarget.querySelector("img")
        if (!img) {
          img = document.createElement("img")
          img.className = "h-full w-full object-contain"
          this.imageThumbTarget.replaceChildren(img)
        }
        if (img.src !== node.image().src) img.src = node.image().src
      }
      if (this.hasRotInputTarget && document.activeElement !== this.rotInputTarget)
        this.rotInputTarget.value = Math.round(node.rotation() || 0)
      if (this.hasFlipBtnTarget) {
        const flipped = node.scaleX() < 0
        this.flipBtnTarget.classList.toggle("border-brand-blue", flipped)
        this.flipBtnTarget.classList.toggle("bg-brand-blue-soft", flipped)
        this.flipBtnTarget.classList.toggle("text-brand-blue", flipped)
        this.flipBtnTarget.classList.toggle("border-line", !flipped)
        this.flipBtnTarget.classList.toggle("text-brand-graphite", !flipped)
      }
    }
    if (isText) this.refreshTextPanel(ed, node)
    this._refreshOverridePanel()
  }

  // Заполнить поля свойств текста по выделению (видимость секции — refreshPanel)
  refreshTextPanel(ed, node) {
    if (!this.hasPanelTextTarget) return
    const cfg = node.getAttr("config") || {}
    // одно свойство может жить в нескольких контролах (color-инпут + hex-текст) — обновляем все
    const set = (name, val) => {
      for (const el of this.panelTextTarget.querySelectorAll(`[data-prop="${name}"]`)) {
        if (document.activeElement === el) continue
        if (el.type === "checkbox") el.checked = !!val
        else el.value = val ?? ""
      }
      if (/(^|\.)color$/.test(name)) this._syncSwatch(name, val)
    }
    // селект шрифта заполняем динамически (из this.fonts)
    const fontSel = this.panelTextTarget.querySelector('[data-prop="font_id"]')
    if (fontSel && !fontSel.dataset.filled) {
      fontSel.innerHTML = this.fonts.map((f) => `<option value="${f.id}">${f.name}</option>`).join("")
      fontSel.dataset.filled = "1"
    }
    set("content", cfg.content)
    if (fontSel) fontSel.value = cfg.font_id ?? ""
    this._refreshFontBtn(cfg.font_id)
    this._refreshAlignButtons(cfg.align || "center")
    // Поле «Размер» показывает то, в чём оператор вводит: кегль или высоту букв.
    const unit = this._sizeUnitOf(cfg)
    const unitSel = this.panelTextTarget.querySelector("[data-size-unit]")
    if (unitSel && document.activeElement !== unitSel) unitSel.value = unit
    set("font_size_mm", unit === "cap"
      ? Math.round(emToCap(cfg.font_size_mm || 0, this._capRatioFor(cfg)) * 10) / 10
      : cfg.font_size_mm)
    set("color", cfg.color)
    set("align", cfg.align)
    set("letter_spacing", cfg.letter_spacing)
    set("max_width_mm", cfg.max_width_mm ?? 0)
    set("stroke.color", cfg.stroke?.color)
    set("stroke.width", cfg.stroke?.width)
    set("inner_stroke.color", cfg.inner_stroke?.color)
    set("inner_stroke.width", cfg.inner_stroke?.width)
    set("shadow.color", cfg.shadow?.color)
    set("shadow.dx", cfg.shadow?.dx)
    set("shadow.dy", cfg.shadow?.dy)
    set("personalization.enabled", cfg.personalization?.enabled)
    set("personalization.field", cfg.personalization?.field)
    set("personalization.sample", cfg.personalization?.sample)
    // поля персонализации видны только при включённом «Переменном тексте»
    this.panelTextTarget.querySelector("[data-pers-fields]")?.classList.toggle("hidden", !cfg.personalization?.enabled)
    // тумблеры Ж/К: значение + disabled, если у шрифта нет такого начертания
    set("bold", cfg.bold)
    set("italic", cfg.italic)
    const f = this.fontById.get(Number(cfg.font_id))
    const files = f?.files || {}
    const boldEl = this.panelTextTarget.querySelector('[data-prop="bold"]')
    const italEl = this.panelTextTarget.querySelector('[data-prop="italic"]')
    if (boldEl) boldEl.disabled = !(files.bold || files.bold_italic)
    if (italEl) italEl.disabled = !(files.italic || files.bold_italic)
    this._refreshSizeHint(cfg)
  }

  // Залить картинку на сервер сразу при вставке и запомнить signed_id на самом
  // <img> — оттуда его подхватят все слои с этой картинкой (addLayer/replaceImage),
  // и сохранение уйдёт ссылкой вместо base64. Падение загрузки не блокирует работу:
  // без signedId слой просто уедет base64-строкой, как раньше.
  async _uploadImage(img) {
    if (!img?._uploadBlob) return
    try {
      // тот же файл на другую деталь — переиспользуем уже залитый блоб, иначе
      // в хранилище копится по копии файла на каждую вставку
      const hash = await blobHash(img._uploadBlob)
      this._blobByHash ||= new Map()
      if (hash && this._blobByHash.has(hash)) { img._signedId = this._blobByHash.get(hash); return }

      const fd = new FormData()
      fd.append("file", img._uploadBlob, img._uploadName || "layer")
      const res = await fetch("/designs/upload_image", {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        body: fd,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      img._signedId = (await res.json()).signed_id
      if (hash) this._blobByHash.set(hash, img._signedId)
    } catch (e) {
      console.warn("[editor] картинку не удалось залить отдельно — уйдёт в base64 при сохранении", e)
    }
  }

  openModal() {
    // уже сохранён — просто обновляем без модалки; новый — спрашиваем имя
    if (this.designIdValue > 0) { this.save(); return }
    this.modalTitleTarget.value = this.designTitle || ""
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    this.modalTitleTarget.focus()
  }

  // Клик по подложке модалки (не по карточке) — закрыть (хендофф)
  closeModalBackdrop(event) {
    if (event.target === this.modalTarget) this.closeModal()
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
  }

  confirmSave() {
    this.designTitle = this.modalTitleTarget.value.trim()
    this.closeModal()
    this.save()
  }

  async save() {
    const layers = []
    for (const ed of this.editors.values()) layers.push(...ed.exportLayers())
    // одна картинка на нескольких деталях = один base64 на весь запрос (иначе ×N к весу)
    const payload = { template: this.templateValue, title: this.designTitle, layers: dedupeImageData(layers) }
    const update = this.designIdValue > 0
    if (!update && this.orderIdValue > 0) payload.order_id = this.orderIdValue   // новый макет в контексте заказа
    if (!update && this.productIdValue > 0) payload.product_id = this.productIdValue // какое изделие шьём (спецификация и цена)
    const bytes = JSON.stringify(payload).length
    console.log("[editor] save", { update, designId: this.designIdValue, layers: layers.length, kbytes: Math.round(bytes / 1024), byPiece: layers.map((l) => `${l.piece}:${l.kind}`) })
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = true
    this.setStatus("Сохранение…")
    try {
      const res = await fetch(update ? `/designs/${this.designIdValue}` : "/designs", {
        method: update ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify(payload),
      })
      console.log("[editor] save response", res.status)
      if (!res.ok) throw new Error(saveErrorMessage(res.status, bytes))
      const data = await res.json()
      console.log("[editor] save ok", data)
      // после создания — переходим на сохранённый макет (надёжно подгружает то, что сохранилось)
      if (!update && data.edit_url) {
        this._setDirty(false)   // иначе beforeunload заблокирует переход
        window.location.assign(data.edit_url)
        return
      }
      this.designIdValue = data.id
      this._setDirty(false)
      this.setStatus("Сохранено ✓")
      return true
    } catch (e) {
      this.setStatus(`Ошибка сохранения: ${e.message}`)
      console.error("[editor] save failed", e)
      return false
    } finally {
      if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = false
    }
  }

  // Протолкнуть выделенный слой во все остальные размеры заказа (связанный слой).
  async applyLayerToSizes() {
    const ed = this.activeEditor
    const node = ed?.selected
    if (!ed || !node) { this.setStatus("Выделите слой."); return }
    // Проверка до save() намеренная: у нового макета save() делает навигацию
    // (window.location.assign) после создания, что оборвёт применение слоя.
    if (!(this.designIdValue > 0)) { this.setStatus("Сначала сохраните макет."); return }
    const linkId = ed.linkIdOf(node)   // гарантируем link_id на ноде до сейва
    await this.save()                  // персистим слой с link_id
    if (!(this.designIdValue > 0)) return
    this.setStatus("Применяю ко всем размерам…")
    try {
      const res = await fetch(`/designs/${this.designIdValue}/apply_layer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ link_id: linkId }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this.setStatus(data.updated > 0 ? `Применено к ${data.updated} размер(ам).` : "Нет других размеров.")
    } catch (e) {
      this.setStatus(`Ошибка применения: ${e.message}`)
      console.error("[editor] applyLayerToSizes failed", e)
    }
  }

  // Автосохранение + серверная синхронизация картинки связанного принта во все размеры,
  // где он уже есть (update-only). Позицию в других размерах не трогает.
  async _syncImageToSizes(linkId) {
    if (!(this.designIdValue > 0)) return   // новый макет: sync после первого ручного сохранения
    const saved = await this.save()
    if (!saved || !(this.designIdValue > 0)) return
    try {
      const res = await fetch(`/designs/${this.designIdValue}/sync_layer_image`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ link_id: linkId }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (data.updated > 0) this.setStatus(`Картинка обновлена в ${data.updated} размер(ах).`)
    } catch (e) {
      this.setStatus(`Ошибка синхронизации: ${e.message}`)
      console.error("[editor] syncImageToSizes failed", e)
    }
  }

  // Продолжить выделенный слой-картинку на выбранную деталь: связанная часть (общий link_id)
  // с глобальным центром источника. Деталь-приёмник клипует свою часть.
  continueToPiece(event) {
    const targetPieceId = event.target.value
    event.target.value = ""   // сбрасываем селект обратно в плейсхолдер
    const srcEd = this.activeEditor
    const node = srcEd?.selected
    if (!srcEd || !node || node.className !== "Image" || !targetPieceId) return
    const targetEd = this.editors.get(targetPieceId)
    if (!targetEd || targetEd === srcEd) { this.setStatus("Выберите другую деталь."); return }

    const linkId = srcEd.linkIdOf(node)
    const groupId = srcEd.ensureGroupId(node)   // источник и копия — в одной группе синхрона
    // часть с этим link_id уже на детали → пересоздадим её на актуальном месте источника
    const existing = targetEd.imageNodes().find((n) => n.getAttr("linkId") === linkId)

    const base = {
      ...srcEd.serializeImage(node),
      image_signed_id: node.getAttr("signedId") || null,
      config: { name: node.getAttr("layerName") || "" },
      hidden: false,
      group_id: groupId,
    }
    // Переход рисунка на соседнюю деталь через боковой шов (см. lib/seam_continuation):
    // слой у правого края переда → у левого края спинки, картинка НЕ переворачивается, припуск
    // сдвигает глубже от края. Перёд/спинка в раскладке разложены встречно.
    const sb = srcEd.piece.bbox, tb = targetEd.piece.bbox
    const gx = (node.x() - srcEd.fit.tx) / srcEd.fit.s   // глобальный центр источника (units)
    const gy = (node.y() - srcEd.fit.ty) / srcEd.fit.s
    const allowanceU = (this.seamAllowanceValue || 0) * 100
    const { cx, cy } = transferToPieceCenter(sb, tb, gx, gy, allowanceU)
    const data = continuationLayerData(base, cx, cy, linkId)

    // --- DEBUG перенос (временно) ---
    const dstPx = targetEd.fit.tx + cx * targetEd.fit.s
    const dstPy = targetEd.fit.ty + cy * targetEd.fit.s
    const r = (v) => Math.round(v)
    const sCx = sb.x + sb.w / 2, sCy = sb.y + sb.h / 2, tCx = tb.x + tb.w / 2, tCy = tb.y + tb.h / 2
    this._dbg(`—— переход ${srcEd.piece.id} → ${targetPieceId}  existing=${!!existing}  припуск ${this.seamAllowanceValue || 0}мм`)
    this._dbg(`src bbox x${r(sb.x)} y${r(sb.y)} w${r(sb.w)} h${r(sb.h)}  центр(${r(sCx)},${r(sCy)})`)
    this._dbg(`dst bbox x${r(tb.x)} y${r(tb.y)} w${r(tb.w)} h${r(tb.h)}  центр(${r(tCx)},${r(tCy)})`)
    this._dbg(`src слой глоб: cx${r(gx)} cy${r(gy)}  (от центра src dx${r(gx - sCx)} dy${r(gy - sCy)})`)
    this._dbg(`итог center (units): cx${r(cx)} cy${r(cy)}  (от центра dst dx${r(cx - tCx)} dy${r(cy - tCy)})  |  внутри dst bbox? x=${cx >= tb.x && cx <= tb.x + tb.w} y=${cy >= tb.y && cy <= tb.y + tb.h}`)
    this._dbg(`ляжет на dst canvas px: x${r(dstPx)} y${r(dstPy)}  (stage ${r(targetEd.stage.width())}×${r(targetEd.stage.height())})`)
    // --- /DEBUG ---

    targetEd.pushHistory()   // снимок ДО удаления — undo вернёт прежнее положение части
    if (existing) {
      if (targetEd.selected === existing) targetEd.select(null)
      existing.destroy()
    }
    const newNode = targetEd.addImageData(node.image(), data)
    targetEd.renderLayers()
    targetEd.select(newNode)
    this.activeEditor = targetEd
    this.refreshPanel(targetEd, newNode)
    this._setDirty(true)
    this.setStatus(existing
      ? `Часть на детали «${targetPieceId}» — местоположение обновлено.`
      : `Продолжено на деталь «${targetPieceId}».`)
  }

  // Отвязать активный слой от группы синхрона (group_id снимаем, link_id остаётся —
  // замена картинки всё ещё синхронна). Дальше слой двигается независимо.
  unlinkGroup() {
    const ed = this.activeEditor, node = ed?.selected
    if (!node || !node.getAttr("groupId")) { this.setStatus("Слой не в группе"); return }
    ed.pushHistory()
    node.setAttr("groupId", null)
    this._setDirty(true)
    this.refreshPanel(ed, node)
    this.setStatus("Слой отвязан от группы")
  }

  // Синхрон группы: члены с тем же groupId на других деталях повторяют scale/rotation источника,
  // позиция — переход на деталь (transferToPieceCenter). Симметрично: srcNode — та копия, что тронули.
  _syncGroup(srcEd, srcNode) {
    if (this._syncing || !srcNode) return
    const gid = srcNode.getAttr("groupId")
    if (!gid) return
    this._syncing = true
    try {
      // Общий undo-токен: если верхний снапшот источника уже помечен (серия коалесцируется,
      // напр. клики масштаба <700мс) — переиспользуем его, иначе новый. Иначе копии копили бы
      // отдельный снапшот на каждый клик → рассинхрон отмены источника и копий.
      const sh = srcEd.history
      let token = sh.length ? sh[sh.length - 1].group : null
      if (!token) { token = crypto.randomUUID(); if (sh.length) sh[sh.length - 1].group = token }
      const gx = (srcNode.x() - srcEd.fit.tx) / srcEd.fit.s
      const gy = (srcNode.y() - srcEd.fit.ty) / srcEd.fit.s
      const allowanceU = (this.seamAllowanceValue || 0) * 100
      // Синхроним ФИЗИЧЕСКИЙ размер (units), а не сырой scaleX/scaleY: у копии масштаб запечён
      // в width при переносе (serializeImage → scaleX≈±1), поэтому сырой scale исказил бы размер.
      // Знак wU/hU несёт флип; каждая копия считает свой scale от своих width/fit.s (верно и в fullscreen).
      const wU = (srcNode.width() * srcNode.scaleX()) / srcEd.fit.s
      const hU = (srcNode.height() * srcNode.scaleY()) / srcEd.fit.s
      const rot = srcNode.rotation()
      for (const ed of this.editors.values()) {
        if (ed === srcEd) continue
        for (const n of ed.imageNodes()) {
          if (n.getAttr("groupId") !== gid) continue
          ed.pushHistory("groupsync:" + token, token)   // коалесс по токену: серия = 1 шаг отмены
          const { cx, cy } = transferToPieceCenter(srcEd.piece.bbox, ed.piece.bbox, gx, gy, allowanceU)
          n.scaleX((wU * ed.fit.s) / n.width())
          n.scaleY((hU * ed.fit.s) / n.height())
          n.rotation(rot)
          n.position({ x: ed.fit.tx + cx * ed.fit.s, y: ed.fit.ty + cy * ed.fit.s })
          ed.layer.draw()
        }
      }
    } finally {
      this._syncing = false
    }
  }

  // DEBUG-замер: положение слоя относительно детали (мм) — чтобы вывести формулу
  // переноса по шву. Дёргается на dragend перетаскиваемого слоя.
  _dbgMeasure(ed, node) {
    const b = ed.piece.bbox
    const gx = (node.x() - ed.fit.tx) / ed.fit.s
    const gy = (node.y() - ed.fit.ty) / ed.fit.s
    const mm = (u) => (u / 100).toFixed(1)
    this._dbg(`[замер] ${ed.piece.id}: от центра dx${mm(gx - (b.x + b.w / 2))} dy${mm(gy - (b.y + b.h / 2))}мм | лев.край ${mm(gx - b.x)} прав.край ${mm(b.x + b.w - gx)}мм | глоб cx${Math.round(gx)} cy${Math.round(gy)}`)
  }

  // Временная экранная debug-панель (для скринов). Пишет строки внизу слева.
  _dbg(msg) {
    this._ensureDbgUI()
    const line = document.createElement("div")
    line.textContent = msg
    this._dbgBody.appendChild(line)
    this._dbgBody.scrollTop = this._dbgBody.scrollHeight
    this._dbgCount = (this._dbgCount || 0) + 1
    this._dbgToggle.textContent = `DBG (${this._dbgCount})`
    console.log("[dbg]", msg)
  }

  // Маленькая кнопка-переключатель слева внизу; сама панель скрыта, открывается по клику.
  _ensureDbgUI() {
    if (this._dbgBody) return
    const btn = document.createElement("button")
    btn.textContent = "DBG (0)"
    btn.style.cssText = "position:fixed;left:8px;bottom:8px;z-index:100000;cursor:pointer;background:rgba(17,24,39,.94);color:#93c5fd;font:700 11px/1 ui-monospace,Menlo,monospace;padding:6px 9px;border:none;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.4)"

    const panel = document.createElement("div")
    panel.style.cssText = "position:fixed;left:8px;bottom:44px;z-index:99999;display:none;width:340px;max-width:62vw;max-height:40vh;overflow:auto;background:rgba(17,24,39,.96);color:#e5e7eb;font:11px/1.4 ui-monospace,Menlo,monospace;padding:8px 10px;border-radius:8px;white-space:pre-wrap;box-shadow:0 4px 16px rgba(0,0,0,.4)"
    const bar = document.createElement("div")
    bar.style.cssText = "display:flex;gap:10px;justify-content:space-between;align-items:center;margin-bottom:6px;font-weight:700;color:#93c5fd"
    const title = document.createElement("span"); title.textContent = "DEBUG перенос"
    const clr = document.createElement("button")
    clr.textContent = "очистить"
    clr.style.cssText = "cursor:pointer;color:#fca5a5;background:none;border:none;font:inherit"
    clr.onclick = () => { this._dbgBody.textContent = ""; this._dbgCount = 0; this._dbgToggle.textContent = "DBG (0)" }
    bar.appendChild(title); bar.appendChild(clr)
    const body = document.createElement("div")
    panel.appendChild(bar); panel.appendChild(body)

    btn.onclick = () => { panel.style.display = panel.style.display === "none" ? "block" : "none" }

    document.body.appendChild(panel)
    document.body.appendChild(btn)
    this._dbgBody = body
    this._dbgToggle = btn
    this._dbgPanel = panel
    this._dbgCount = 0
  }

  disconnect() {
    this._dbgPanel?.remove(); this._dbgToggle?.remove()   // не оставлять debug-UI при turbo-переходе
    this.closeReuse()
    this.closeFontMenu()
    this._ro?.disconnect()
    clearTimeout(this._relayoutTimer)
    window.removeEventListener("keydown", this.keyHandler)
    window.removeEventListener("keyup", this.keyUpHandler)
    window.removeEventListener("beforeunload", this._beforeUnload)
    document.removeEventListener("turbo:before-visit", this._beforeVisit)
    for (const ed of this.editors.values()) ed.destroy()
  }

  // Несохранённые правки: точка на «Сохранить», confirm при уходе/переключении макета
  _setDirty(on) {
    this._dirty = !!on
    if (this.hasDirtyDotTarget) this.dirtyDotTarget.classList.toggle("hidden", !this._dirty)
  }

  // --- Single-screen раскладка: карточка фитится в main, холст flex:1 ---

  // --card-h на main: высота видимой области минус вертикальные паддинги
  _setCardHeight() {
    if (!this.hasMainTarget) return
    const m = this.mainTarget
    const cs = getComputedStyle(m)
    const h = m.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom)
    m.style.setProperty("--card-h", `${Math.max(h, 320)}px`)
  }

  // Общие w/h холста (карточки однотипны) + единый масштаб: 1мм = одинаковые px везде
  _measureShared() {
    const box = this.canvasTargets[0]
    const w = Math.floor(box.clientWidth) || 340
    const h = Math.floor(box.clientHeight) || Math.round(w * 1.35)
    const pad = Math.max(12, Math.min(w, h) * 0.04)
    let scale = Infinity
    for (const p of this.pieces.values())
      scale = Math.min(scale, (w - 2 * pad) / p.bbox.w, (h - 2 * pad) / p.bbox.h)
    return { w, h, scale }
  }

  // Перестройка всех деталей при изменении размеров main (окно, панель свойств)
  _observeResize() {
    if (!this.hasMainTarget || typeof ResizeObserver === "undefined") return
    this._ro = new ResizeObserver(() => {
      clearTimeout(this._relayoutTimer)
      this._relayoutTimer = setTimeout(() => this._relayout(), 150)
    })
    this._ro.observe(this.mainTarget)
  }

  _relayout() {
    if (this._maxEditor) return   // в полноэкранном режиме размером управляет maximize()
    this._setCardHeight()
    const { w, h, scale } = this._measureShared()
    if (Math.abs(w - this.cardW) < 2 && Math.abs(h - this.cardH) < 2) return
    this.cardW = w; this.cardH = h; this.sharedScale = scale
    for (const ed of this.editors.values()) ed.resize(w, h, scale)
  }

  // --- Правая панель свойств: 368px ↔ 52px ---
  togglePanel() {
    if (!this.hasAsideTarget) return
    this._panelCollapsed = !this._panelCollapsed
    this.asideTarget.classList.toggle("w-[368px]", !this._panelCollapsed)
    this.asideTarget.classList.toggle("w-[52px]", this._panelCollapsed)
    this.panelExpandedTarget.classList.toggle("flex", !this._panelCollapsed)
    this.panelExpandedTarget.classList.toggle("hidden", this._panelCollapsed)
    this.panelCollapsedTarget.classList.toggle("flex", this._panelCollapsed)
    this.panelCollapsedTarget.classList.toggle("hidden", !this._panelCollapsed)
    // ширина main изменилась — ResizeObserver сам перестроит холсты
  }

  // pieceId -> { id, path2d, bbox, d, points, isPolygon }
  async loadContours() {
    const text = await (await fetch(this.urlValue)).text()
    const doc = new DOMParser().parseFromString(text, "image/svg+xml")
    const svg = doc.querySelector("svg")

    const holder = document.createElement("div")
    holder.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none"
    holder.appendChild(svg)
    document.body.appendChild(holder)

    const pieces = new Map()
    for (const el of svg.querySelectorAll("[data-piece]")) {
      const b = el.getBBox()
      // Примитивы (rect планки воротника, circle, ellipse, polygon) рисуем
      // ломаной по тем же точкам, что считает сервер; путь — по атрибуту d.
      const points = shapePoints(el)
      pieces.set(el.dataset.piece, {
        id: el.dataset.piece,
        path2d: toPath2D(el),
        bbox: { x: b.x, y: b.y, w: b.width, h: b.height },
        d: points ? null : el.getAttribute("d"),
        points,
        isPolygon: points !== null,
      })
    }
    document.body.removeChild(holder)
    if (!pieces.size) throw new Error("в SVG нет элементов с data-piece")
    return pieces
  }

  // --- Кнопки: находим деталь по data-piece ---
  editorFor(event) {
    const el = event.currentTarget || event.target
    return this.editors.get(el.dataset.piece)
  }

  addLayer(event) {
    const ed = this.editorFor(event)
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    if (!files.length) return
    const total = files.length
    let done = 0, ok = 0
    this.showLoading(total)
    const step = () => {
      done++
      this.updateLoading(done, total)
      if (done < total) return
      this.hideLoading()
      this.setStatus(ok === total ? `Добавлено слоёв: ${ok}.` : `Добавлено слоёв: ${ok} из ${total}.`)
    }
    for (const file of files) readImage(file, async (img) => {
      if (img) {
        await this._uploadImage(img)
        const node = ed?.addLayer(img)
        if (node && file.name) {
          node.setAttr("layerName", file.name)
          ed.renderLayers()
          this.refreshPanel(ed, node)   // имя файла в хедер панели (addLayer уже отрисовал её без имени)
        }
        ok++
      }
      step()
    })
  }

  openReuse(event) {
    const btn = event.currentTarget
    const pieceId = btn.dataset.piece
    const pop = btn.parentElement.querySelector("[data-reuse-popover]")
    if (!pop) return
    // повторный клик по той же кнопке — закрыть
    if (!pop.classList.contains("hidden")) { this.closeReuse(); return }
    this.closeReuse()

    // картинки со ВСЕХ деталей, кроме текущей; дедуп по src; с геометрией для переноса масштаба
    const seen = new Set()
    const items = []
    for (const [pid, ed] of this.editors) {
      if (pid === pieceId) continue
      for (const node of ed.imageNodes()) {
        const img = node.image()
        if (!img || seen.has(img.src)) continue
        seen.add(img.src)
        items.push({ img, data: ed.serializeImage(node), linkId: ed.linkIdOf(node) })
      }
    }

    pop.innerHTML = ""
    if (!items.length) {
      const empty = document.createElement("p")
      empty.className = "px-1 py-2 text-xs text-brand-muted"
      empty.textContent = "На других деталях пока нет слоёв"
      pop.appendChild(empty)
    } else {
      const grid = document.createElement("div")
      grid.className = "grid grid-cols-3 gap-2"
      for (const item of items) {
        const tile = document.createElement("button")
        tile.type = "button"
        tile.className = "aspect-square overflow-hidden rounded border border-gray-200 hover:border-brand-blue"
        const thumb = document.createElement("img")
        thumb.src = item.img.src
        thumb.className = "h-full w-full object-contain"
        tile.appendChild(thumb)
        tile.addEventListener("click", () => {
          this.editors.get(pieceId)?.addImageAt(item.img, item.data, item.linkId)
          this.closeReuse()
          this.setStatus("Слой добавлен с другой детали (масштаб сохранён).")
        })
        grid.appendChild(tile)
      }
      pop.appendChild(grid)
    }

    pop.classList.remove("hidden")
    this._reusePopover = pop
    this._reuseOutside = (e) => { if (!pop.contains(e.target) && e.target !== btn) this.closeReuse() }
    this._reuseEsc = (e) => { if (e.key === "Escape") this.closeReuse() }
    // отложенная подписка, чтобы текущий клик не закрыл поповер сразу
    setTimeout(() => {
      document.addEventListener("click", this._reuseOutside)
      document.addEventListener("keydown", this._reuseEsc)
    }, 0)
  }

  closeReuse() {
    if (this._reusePopover) { this._reusePopover.classList.add("hidden"); this._reusePopover = null }
    if (this._reuseOutside) document.removeEventListener("click", this._reuseOutside)
    if (this._reuseEsc) document.removeEventListener("keydown", this._reuseEsc)
    this._reuseOutside = null; this._reuseEsc = null
  }

  setFill(event) {
    const ed = this.editorFor(event)
    if (!ed) return
    ed.pushHistory("fill")
    ed.setFill(event.target.value)
  }
  clearFill(event) {
    const ed = this.editorFor(event)
    if (!ed) return
    ed.pushHistory()
    ed.clearFill()
  }
  coverPiece(event) { this.editorFor(event)?.coverSelected() }
  recenter(event) { this.editorFor(event)?.recenterSelected() }
  scaleUp(event) { this.editorFor(event)?.scaleSelected(1.15) }
  scaleDown(event) { this.editorFor(event)?.scaleSelected(1 / 1.15) }
  zoomIn(event) { this.editorFor(event)?.zoomStep(1.25) }
  zoomOut(event) { this.editorFor(event)?.zoomStep(1 / 1.25) }
  zoomReset(event) { this.editorFor(event)?.resetView() }

  toggleLayers(event) {
    const ed = this.editorFor(event)
    const ul = event.currentTarget.closest("section")?.querySelector("[data-layer-list]")
    if (!ul) return
    ul.classList.toggle("hidden")
    const open = !ul.classList.contains("hidden")
    event.currentTarget.querySelector("[data-layers-chevron]")?.classList.toggle("rotate-180", open)
    if (open) ed?.renderLayers()
  }
  changeTemplate(event) { window.location.assign(`/editor?template=${encodeURIComponent(event.target.value)}`) }

  // «Изделие» при редактировании: переход на другой макет заказа (value = edit-URL).
  // Спрашиваем только при несохранённых правках.
  switchDesign(event) {
    if (this._dirty && !confirm("Переключиться на другой макет? Несохранённые изменения будут потеряны.")) {
      // вернуть селект на текущий макет
      event.target.value = [...event.target.options].find((o) => o.defaultSelected)?.value ?? event.target.value
      return
    }
    this._setDirty(false)   // переход подтверждён — beforeunload не должен спросить второй раз
    window.location.assign(event.target.value)
  }
  deleteSelected(event) { this.editorFor(event)?.deleteSelected() }

  // --- Режим получателя ---
  // В обычном режиме персональные надписи показывают образец («ИВАНОВ»). Оператору нужно
  // увидеть реальные фамилии до раскладки — селект «Показать как» подставляет данные
  // конкретного получателя во все персональные слои сразу.

  async _loadRecipients() {
    this.previewRecipient = null
    this.recipientsById = new Map()
    this.overridesByKey = new Map()
    if (!this.hasRecipientSelectTarget || !this.recipientsUrlValue) return
    await this._loadOverrides()
    const url = `${this.recipientsUrlValue}?size=${encodeURIComponent(this.designSizeValue || "")}`
    const data = await fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
    const rows = data?.recipients || []
    this.recipientsById = new Map(rows.map((r) => [r.id, r]))
    const sel = this.recipientSelectTarget
    sel.innerHTML = `<option value="">— образец —</option>` +
      rows.map((r) => `<option value="${r.id}">${this._recipientLabel(r)}</option>`).join("")
    if (!rows.length) {
      sel.disabled = true
      sel.title = `Нет получателей размера ${this.designSizeValue || "—"}`
      return
    }
    // deep-link из отчёта «не влезло»: сразу открыть макет глазами этого получателя
    const wanted = Number(this.previewRecipientIdValue)
    if (wanted && this.recipientsById.has(wanted)) {
      sel.value = String(wanted)
      this._applyRecipient(this.recipientsById.get(wanted))
    }
    this._refreshOverflowBadge()
  }

  _recipientLabel(r) {
    const num = r.number ? ` №${r.number}` : ""
    return `${r.name || "(без имени)"}${num}`
  }

  // Бейдж «Не влезло: N» — считаем целиком в браузере: шрифты уже в loadedFonts,
  // ширины меряет тот же textToPath, порог — тот же lib/text_fit.js, что и на сервере.
  // Поэтому цифра совпадает с отчётом на странице заказа без похода на сервер.
  _refreshOverflowBadge() {
    if (!this.hasOverflowBadgeTarget) return
    const bad = []
    for (const ed of this.editors.values()) {
      for (const node of ed.clipGroup.getChildren()) {
        if (!ed.isPersonalNode(node)) continue
        const cfg = node.getAttr("config")
        const maxWpx = (cfg.max_width_mm || 0) * 100 * ed.fit.s
        if (maxWpx <= 0) continue
        const otf = this.loadedFonts.get(`${Number(cfg.font_id)}:${styleKey(cfg)}`)
                 || this.loadedFonts.get(`${Number(cfg.font_id)}:regular`)
        if (!otf) continue
        const sizePx = (cfg.font_size_mm || 50) * 100 * ed.fit.s
        const spacingPx = (cfg.letter_spacing || 0) * 100 * ed.fit.s
        for (const r of this.recipientsById.values()) {
          const text = resolveText(cfg, r)
          if (!text) continue
          const { width } = textToPath(otf, text, sizePx, spacingPx)
          if (isOverflow(width, maxWpx, cfg.font_size_mm)) bad.push(r)
        }
      }
    }
    this._overflowRecipients = bad
    const btn = this.overflowBadgeTarget
    btn.classList.toggle("hidden", bad.length === 0)
    btn.classList.toggle("inline-flex", bad.length > 0)
    btn.textContent = `Не влезло: ${bad.length}`
    btn.title = bad.map((r) => this._recipientLabel(r)).join(", ")
  }

  // Клик по бейджу — показать первого проблемного получателя
  showFirstOverflow() {
    const first = this._overflowRecipients?.[0]
    if (!first) return
    if (this.hasRecipientSelectTarget) this.recipientSelectTarget.value = String(first.id)
    this._applyRecipient(first)
  }

  switchRecipient(event) {
    const id = Number(event.target.value)
    this._applyRecipient(id ? this.recipientsById.get(id) : null)
  }

  _applyRecipient(recipient) {
    this.previewRecipient = recipient || null
    for (const ed of this.editors.values()) ed.setRecipientMode(!!this.previewRecipient)
    this._rerenderTexts()
    this._refreshRecipientBar()
    this._refreshOverridePanel()
  }

  // Перестроить все текстовые слои под нового получателя. replaceTextNode сохраняет
  // позицию/поворот/z и выделение, так что достаточно скормить ему тот же config.
  _rerenderTexts() {
    for (const ed of this.editors.values()) {
      for (const node of [...ed.clipGroup.getChildren()]) {
        if (!ed.isTextNode(node)) continue
        if (!node.getAttr("config")?.personalization?.enabled) continue
        ed.replaceTextNode(node, node.getAttr("config"))
      }
    }
  }

  // --- Персональные правки ---
  // Ключ — (получатель, логический слой, деталь). Локальный кэш держим,
  // чтобы перестроение узла сразу подхватывало свежую правку без похода на сервер.

  _ovKey(linkId, piece) { return `${this.previewRecipient?.id}|${linkId}|${piece}` }

  overrideFor(linkId, piece) {
    if (!this.previewRecipient || !linkId) return null
    return this.overridesByKey?.get(this._ovKey(linkId, piece)) || null
  }

  async _loadOverrides() {
    this.overridesByKey = new Map()
    if (!this.overridesUrlValue) return
    const url = `${this.overridesUrlValue}?size=${encodeURIComponent(this.designSizeValue || "")}`
    const data = await fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
    for (const o of data?.overrides || []) {
      this.overridesByKey.set(`${o.recipient_id}|${o.link_id}|${o.piece}`, o)
    }
  }

  // patch — то, что изменилось (смещение или масштаб); остальное берём из текущей правки
  async saveOverride(linkId, piece, patch) {
    if (!this.previewRecipient || !linkId) return
    const cur = this.overrideFor(linkId, piece) || { dx_mm: 0, dy_mm: 0, font_scale: 1 }
    const next = { ...cur, ...patch, recipient_id: this.previewRecipient.id, link_id: linkId, piece }
    const isDefault = !next.dx_mm && !next.dy_mm && Math.abs((next.font_scale ?? 1) - 1) < 1e-6
    if (isDefault) this.overridesByKey.delete(this._ovKey(linkId, piece))
    else this.overridesByKey.set(this._ovKey(linkId, piece), next)
    this._refreshOverridePanel()

    const res = await fetch(this.overridesUrlValue, {
      method: "POST", headers: this._jsonHeaders(), body: JSON.stringify(next),
    }).catch(() => null)
    if (!res?.ok) this.setStatus("Не удалось сохранить персональную правку")
  }

  // Поля X/Y/кегль в панели правки
  updateOverrideProp(event) {
    const ed = this.activeEditor
    const node = ed?.selected
    if (!node || !this.previewRecipient) return
    const prop = event.target.dataset.ovProp
    if (!prop) return
    const value = parseFloat(event.target.value) || (prop === "font_scale" ? 1 : 0)
    this.saveOverride(node.getAttr("linkId"), ed.piece.id, { [prop]: value })
    ed.replaceTextNode(node, node.getAttr("config"))
  }

  resetOverride() {
    const ed = this.activeEditor
    const node = ed?.selected
    if (!node || !this.previewRecipient) return
    this.saveOverride(node.getAttr("linkId"), ed.piece.id, { dx_mm: 0, dy_mm: 0, font_scale: 1 })
    ed.replaceTextNode(node, node.getAttr("config"))
  }

  // Секция «Правка для получателя» — видна только в режиме получателя на персональном слое
  _refreshOverridePanel() {
    if (!this.hasPanelOverrideTarget) return
    const ed = this.activeEditor
    const node = ed?.selected
    const show = !!this.previewRecipient && !!node && ed.isPersonalNode(node)
    this.panelOverrideTarget.classList.toggle("hidden", !show)
    if (!show) return
    const ov = this.overrideFor(node.getAttr("linkId"), ed.piece.id) || { dx_mm: 0, dy_mm: 0, font_scale: 1 }
    for (const el of this.panelOverrideTarget.querySelectorAll("[data-ov-prop]")) {
      if (document.activeElement === el) continue
      const prop = el.dataset.ovProp
      const raw = ov[prop] ?? (prop === "font_scale" ? 1 : 0)
      el.value = prop === "font_scale" ? raw : Math.round(raw * 10) / 10
    }
    if (this.hasOverrideScaleLabelTarget) {
      this.overrideScaleLabelTarget.textContent = `${Math.round((ov.font_scale ?? 1) * 100)} %`
    }
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
    }
  }

  _refreshRecipientBar() {
    if (!this.hasRecipientBarTarget) return
    const r = this.previewRecipient
    this.recipientBarTarget.classList.toggle("hidden", !r)
    this.recipientBarTarget.classList.toggle("flex", !!r)
    if (r && this.hasRecipientBarTextTarget) {
      this.recipientBarTextTarget.textContent =
        `Режим получателя: ${this._recipientLabel(r)} (${r.size}). ` +
        "Правки положения сохраняются только для него."
    }
  }

  // Развернуть деталь на весь экран. Кнопка скрыта — вход по двойному клику на холсте
  // (dblclick вешает PieceEditor), выход — Esc или крестик оверлея.
  maximize(event) { this.maximizePiece(event.currentTarget.dataset.piece) }

  maximizePiece(pieceId) {
    if (this._maxEditor || !this.hasOverlayTarget) return
    const ed = this.editors.get(pieceId)
    const section = ed?.stage.container().closest("section")
    if (!ed || !section) return

    // запоминаем место карточки, чтобы вернуть её обратно
    this._maxPlaceholder = document.createComment("piece-card-slot")
    section.parentNode.insertBefore(this._maxPlaceholder, section)
    this._maxSection = section
    this._maxEditor = ed
    section.classList.add("mx-auto", "w-full")

    // переносим в оверлей саму панель управления (чтобы её контролы были доступны) и деталь
    const toolbar = this.element.querySelector("header")
    if (toolbar && this.hasOverlayBarTarget) {
      this._toolbarPlaceholder = document.createComment("toolbar-slot")
      toolbar.parentNode.insertBefore(this._toolbarPlaceholder, toolbar)
      this._toolbar = toolbar
      this.overlayBarTarget.appendChild(toolbar)
    }
    this.overlaySlotTarget.appendChild(section)
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("flex")

    const W = Math.min(window.innerWidth - 48, 1100)
    const H = Math.max(320, window.innerHeight - 220)
    const canvasDiv = ed.stage.container()
    canvasDiv.style.width = `${W}px`
    canvasDiv.style.height = `${H}px`
    section.style.maxWidth = `${W + 24}px`
    ed.fullscreen = true
    ed.resize(W, H)   // масштаб «по детали» — крупнее, чем в сетке
  }

  closeMaximize() {
    const ed = this._maxEditor, section = this._maxSection
    if (!ed || !section) return
    const canvasDiv = ed.stage.container()
    canvasDiv.style.width = ""
    canvasDiv.style.height = ""   // высоту отдаёт flex:1 внутри карточки
    section.style.maxWidth = ""
    section.classList.remove("mx-auto", "w-full")
    ed.fullscreen = false
    ed.resize(this.cardW, this.cardH, this.sharedScale)   // обратно общий масштаб

    // вернуть панель управления и деталь на места
    if (this._toolbar && this._toolbarPlaceholder) {
      this._toolbarPlaceholder.parentNode.insertBefore(this._toolbar, this._toolbarPlaceholder)
      this._toolbarPlaceholder.remove()
    }
    this._maxPlaceholder.parentNode.insertBefore(section, this._maxPlaceholder)
    this._maxPlaceholder.remove()
    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("flex")
    this._maxEditor = this._maxSection = this._maxPlaceholder = null
    this._toolbar = this._toolbarPlaceholder = null
  }

  toggleGrid() {
    this.gridOn = !this.gridOn
    for (const ed of this.editors.values()) ed.setGrid(this.gridOn)
    if (this.hasGridBtnTarget) this._setToggleState(this.gridBtnTarget, this.gridOn)
  }

  toggleRuler() {
    this.rulerOn = !this.rulerOn
    for (const ed of this.editors.values()) ed.setRuler(this.rulerOn)
    if (this.hasRulerBtnTarget) this._setToggleState(this.rulerBtnTarget, this.rulerOn)
  }

  toggleMargin() {
    this.marginOn = !this.marginOn
    for (const ed of this.editors.values()) ed.setMargin(this.marginOn)
    if (this.hasMarginBtnTarget) this._setToggleState(this.marginBtnTarget, this.marginOn)
  }

  // Иконка-тоггл: вкл → border accent + bg accent-soft + цвет accent-hover (хендофф).
  _setToggleState(btn, on) {
    btn.setAttribute("aria-pressed", on ? "true" : "false")
    btn.classList.toggle("border-brand-blue", on)
    btn.classList.toggle("bg-brand-blue-soft", on)
    btn.classList.toggle("text-brand-blue-dark", on)
    btn.classList.toggle("border-line", !on)
    btn.classList.toggle("bg-white", !on)
    btn.classList.toggle("text-brand-graphite", !on)
  }

  setGridStep(event) {
    const mm = parseInt(event.target.value, 10)
    if (this.hasGridValTarget) this.gridValTarget.textContent = `${mm} мм`
    for (const ed of this.editors.values()) ed.setGridMm(mm)
  }

  // Заполнить/очистить поля размера по текущему выделению детали ed
  refreshSize(ed, node) {
    if (!this.hasSizeWTarget) return
    const mm = (ed && node && node.className === "Image") ? ed.sizeMm() : null
    const on = !!mm
    this.sizeWTarget.disabled = !on
    this.sizeHTarget.disabled = !on
    if (document.activeElement !== this.sizeWTarget) this.sizeWTarget.value = on ? mm.w : ""
    if (document.activeElement !== this.sizeHTarget) this.sizeHTarget.value = on ? mm.h : ""
  }

  setSizeW(event) {
    const mm = parseFloat(event.target.value)
    if (!this.activeEditor || !(mm > 0)) return
    this.activeEditor.applySizeMm("w", mm, this.ratioLocked)
    if (this.ratioLocked) this.sizeHTarget.value = this.activeEditor.sizeMm().h
  }

  setSizeH(event) {
    const mm = parseFloat(event.target.value)
    if (!this.activeEditor || !(mm > 0)) return
    this.activeEditor.applySizeMm("h", mm, this.ratioLocked)
    if (this.ratioLocked) this.sizeWTarget.value = this.activeEditor.sizeMm().w
  }

  // Заполнить/очистить поля позиции (X/Y есть и в панели картинки, и в панели текста)
  refreshPos(ed, node) {
    if (!this.hasPosXTarget) return
    const off = (ed && node) ? ed.offsetMm() : null
    const on = !!off
    for (const el of this.posXTargets) {
      el.disabled = !on
      if (document.activeElement !== el) el.value = on ? off.x : ""
    }
    for (const el of this.posYTargets) {
      el.disabled = !on
      if (document.activeElement !== el) el.value = on ? off.y : ""
    }
  }

  setPosX(event) {
    const mm = parseFloat(event.target.value)
    if (!this.activeEditor || !Number.isFinite(mm)) return
    this.activeEditor.applyOffsetMm("x", mm)
  }

  setPosY(event) {
    const mm = parseFloat(event.target.value)
    if (!this.activeEditor || !Number.isFinite(mm)) return
    this.activeEditor.applyOffsetMm("y", mm)
  }

  toggleLock() {
    this.ratioLocked = !this.ratioLocked
    for (const ed of this.editors.values()) ed.setKeepRatio(this.ratioLocked)
    if (this.hasLockBtnTarget) {
      this.lockBtnTarget.textContent = this.ratioLocked ? "🔒" : "🔓"
      this.lockBtnTarget.setAttribute("aria-pressed", this.ratioLocked ? "true" : "false")
      this.lockBtnTarget.classList.toggle("border-brand-blue", this.ratioLocked)
      this.lockBtnTarget.classList.toggle("bg-brand-blue-soft", this.ratioLocked)
      this.lockBtnTarget.classList.toggle("border-line", !this.ratioLocked)
    }
  }

  // Переименование слоя-картинки из хедера панели (персистится в config.name)
  renameLayer(event) {
    const ed = this.activeEditor
    const node = ed?.selected
    if (!node || node.className !== "Image") return
    node.setAttr("layerName", event.target.value.trim())
    ed.renderLayers()
  }

  // Клик по «Заменить картинку» → открыть скрытый file input этой панели
  replaceImage(event) {
    const input = event.currentTarget.closest("[data-editor-target='panelImage']")
                    ?.querySelector("[data-replace-input]")
    input?.click()
  }

  // Выбран новый файл → подменить изображение выделенного слоя
  onReplaceFile(event) {
    const ed = this.activeEditor
    const node = ed?.selected
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!ed || !node || node.className !== "Image" || !file) return
    readImage(file, async (img) => {
      if (!img) { this.setStatus("Не удалось загрузить картинку."); return }
      await this._uploadImage(img)
      const linkId = ed.linkIdOf(node)
      // заменить на всех связанных нодах во всех деталях текущего макета;
      // общий group-токен → один связанный шаг отмены across деталей
      const group = crypto.randomUUID()
      let count = 0
      for (const e of this.editors.values()) {
        for (const n of e.imageNodes()) {
          if (n.getAttr("linkId") === linkId) { e.replaceImage(n, img, group); count++ }
        }
        e.renderLayers()
      }
      this.refreshPanel(ed, node)
      this._setDirty(true)
      this.setStatus(count > 1 ? `Картинка заменена (${count} дет.).` : "Картинка заменена.")
      await this._syncImageToSizes(linkId)
    })
  }

  // Панель картинки: абсолютный поворот (°) и тумблер «Отразить»
  setRotation(event) {
    const deg = parseFloat(event.target.value)
    if (!this.activeEditor || !Number.isFinite(deg)) return
    this.activeEditor.setRotationSelected(deg)
  }

  // Панель картинки: тумблер «Отразить»
  toggleFlip() {
    if (!this.activeEditor) return
    this.activeEditor.flipSelected()
    this.refreshPanel(this.activeEditor, this.activeEditor.selected)
  }

  // Статус показываем в плавающей плашке и через ~2.5с плавно гасим (opacity → clear),
  // чтобы не висел и не мешал. Новый вызов сбрасывает предыдущий таймер.
  setStatus(text) {
    if (!this.hasStatusTarget) return
    clearTimeout(this._statusTimer)
    clearTimeout(this._statusClearTimer)
    this.statusTarget.classList.remove("opacity-0")
    this.statusTarget.textContent = text
    if (!text) return
    this._statusTimer = setTimeout(() => {
      this.statusTarget.classList.add("opacity-0")
      this._statusClearTimer = setTimeout(() => {
        this.statusTarget.textContent = ""
        this.statusTarget.classList.remove("opacity-0")
      }, 500)
    }, 2500)
  }

  // Отмена: по кнопке детали (event с data-piece) или Ctrl+Z (активная/последняя правленая деталь)
  undo(event) {
    const ed = (event && this.editorFor(event)) || this.activeEditor || this._lastEditor
    if (!ed) { this.setStatus("Нечего отменять"); this._refreshUndoBtn(); return }
    const group = ed.peekGroup()
    if (!ed.undo()) { this.setStatus("Нечего отменять"); this._refreshUndoBtn(); return }
    if (group) {
      for (const other of this.editors.values()) {
        if (other !== ed && other.peekGroup() === group) other.undo()
      }
    }
    this.setStatus("Отменено")
    this._refreshUndoBtn()
  }

  _refreshUndoBtn() {
    if (!this.hasUndoBtnTarget) return
    const ed = this.activeEditor || this._lastEditor
    this.undoBtnTarget.disabled = !(ed && ed.canUndo())
  }

  // Индикатор загрузки слоёв: показываем баннер с прогрессом N/M, пока качаются картинки.
  showLoading(total) {
    if (!this.hasLoadingTarget || total <= 0) return
    this.updateLoading(0, total)
    this.loadingTarget.classList.remove("hidden")
    this.loadingTarget.classList.add("flex")
  }

  updateLoading(loaded, total) {
    if (this.hasLoadingTextTarget) this.loadingTextTarget.textContent = `Загрузка слоёв ${loaded}/${total}…`
  }

  hideLoading() {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.add("hidden")
    this.loadingTarget.classList.remove("flex")
  }
}

// --- Редактор одной детали ---
class PieceEditor {
  constructor(container, piece, width, height, { scale, onSelect, onHistory, editorCtl } = {}) {
    this.piece = piece
    this.onSelect = onSelect
    this.onHistory = onHistory
    this.editorCtl = editorCtl   // ссылка на Stimulus-контроллер: доступ к fonts/loadedFonts
    this.history = []          // снимки состояния детали ДО изменения (per-piece undo)
    this.HISTORY_LIMIT = 50
    this.gridMm = 75   // сетка: 100 units = 1 мм
    this.snap = false
    this._rulerOn = false
    this._marginOn = false
    this.zoom = 1
    this.panX = 0
    this.panY = 0
    this.fullscreen = false
    // DOM-индикатор процента зума в футере этой детали (может отсутствовать)
    this.zoomIndicator = container.closest("section")?.querySelector("[data-zoom-indicator]") || null
    this.layerList = container.closest("section")?.querySelector("[data-layer-list]") || null
    this.layerCount = container.closest("section")?.querySelector("[data-layer-count]") || null

    this.stage = new Konva.Stage({ container, width, height })
    this.layer = new Konva.Layer()
    this.stage.add(this.layer)

    // центр слоя держим в холсте (не потерять) + привязка к сетке, если включена
    this.dragBound = (pos) => {
      // Разрешаем уводить центр слоя за край холста на его ширину/высоту — крупные слои
      // и продолжение через шов требуют позиций у самого края/за краем детали. Слой всё
      // равно обрезается по детали (clipGroup) и остаётся в списке слоёв — не теряется.
      let x = clamp(pos.x, -this.width, 2 * this.width)
      let y = clamp(pos.y, -this.height, 2 * this.height)
      if (this.snap) {
        // снап в локальных координатах стейджа, затем обратно в экранные px
        const lx = (x - this.panX) / this.zoom, ly = (y - this.panY) / this.zoom
        x = snapTo(lx, this.gridOrigin.x, this.gridStep) * this.zoom + this.panX
        y = snapTo(ly, this.gridOrigin.y, this.gridStep) * this.zoom + this.panY
      }
      return { x, y }
    }
    this.stage.on("click tap", (e) => { if (e.target === this.stage) this.select(null) })
    // двойной клик по пустому холсту — полноэкранный режим детали (кнопка скрыта)
    this.stage.on("dblclick dbltap", (e) => {
      if (e.target === this.stage && !this.fullscreen) this.editorCtl?.maximizePiece(this.piece.id)
    })
    this.stage.on("wheel", (e) => {
      // в карточке зум только с Ctrl/Cmd (обычное колесо скроллит страницу),
      // в полноэкране — простым колесом
      if (!this.fullscreen && !(e.evt.ctrlKey || e.evt.metaKey)) return
      e.evt.preventDefault()
      const pointer = this.stage.getPointerPosition()
      if (!pointer) return
      this.applyZoom(e.evt.deltaY < 0 ? 1.1 : 1 / 1.1, pointer)
    })

    this._layoutFit(width, height, scale)
    this._buildScene()
    this.layer.draw()
  }

  // Размер/масштаб под текущий холст (scale задан — общий для всех деталей; иначе по этой детали)
  _layoutFit(width, height, scale) {
    const b = this.piece.bbox
    const pad = Math.max(12, Math.min(width, height) * 0.04)
    const s = scale || Math.min((width - 2 * pad) / b.w, (height - 2 * pad) / b.h)
    this.width = width
    this.height = height
    this.fit = { s, tx: (width - b.w * s) / 2 - b.x * s, ty: (height - b.h * s) / 2 - b.y * s }
    this.gridOrigin = { x: width / 2, y: height / 2 }
    this.gridStep = this.gridMm * 100 * s
  }

  // Построение сцены (клип, контур, трансформер, сетка, линейки) — для конструктора и resize()
  _buildScene() {
    const { s, tx, ty } = this.fit
    const clip = new Path2D()
    clip.addPath(this.piece.path2d, new DOMMatrix([s, 0, 0, s, tx, ty]))
    this.clipGroup = new Konva.Group({ clipFunc: () => [clip] })
    this.layer.add(this.clipGroup)

    const cg = new Konva.Group({ x: tx, y: ty, scaleX: s, scaleY: s, listening: false })
    const cnode = this.piece.isPolygon
      ? new Konva.Line({ points: this.piece.points, closed: true })
      : new Konva.Path({ data: this.piece.d })
    cnode.stroke("#1b1f2a"); cnode.strokeWidth(1.5); cnode.strokeScaleEnabled(false)
    cg.add(cnode)
    this.layer.add(cg)
    this.contourGroup = cg

    this.transformer = new Konva.Transformer({
      rotateEnabled: true, keepRatio: true,
      enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
    })
    this.layer.add(this.transformer)
    // снимок ДО трансформации ручками — для undo (один на всю серию transform)
    this.transformer.on("transformstart", () => this.pushHistory())
    this.transformer.on("transformend", () => this.editorCtl?._syncGroup?.(this, this.selected))
    // тянем ручками — поля размера в шапке обновляются вживую
    this.transformer.on("transform transformend", () => {
      if (this.onSelect) this.onSelect(this, this.selected)
    })
    // текст ручками: по окончании «запекаем» масштаб группы в font_size_mm/letter_spacing
    // и перестраиваем (scaleX/Y узла не сохраняются — только config), иначе размер теряется на save
    this.transformer.on("transformend", () => {
      const n = this.selected
      if (!n || !this.isTextNode(n)) return
      const k = (n.scaleX() + n.scaleY()) / 2   // keepRatio → scaleX≈scaleY
      if (Math.abs(k - 1) < 1e-3) return
      const cfg = JSON.parse(JSON.stringify(n.getAttr("config") || {}))
      cfg.font_size_mm = Math.max(1, (cfg.font_size_mm || 50) * k)
      // Растянули рамкой — кегль изменился, значит изменилась и физическая высота.
      // Без этого поле продолжит показывать старые 150 мм при уже другом кегле.
      if (cfg.size_unit === "cap") {
        // Мерим шрифт явно: _capRatioFor(null) молча отдал бы FALLBACK_RATIO 0.7,
        // cap_height_mm записался бы по выдуманной метрике, а следующая правка
        // свойства пересчитала бы кегль уже по настоящей — надпись бы прыгнула.
        const font = this.editorCtl?._fontForCfg?.(cfg)
        if (font) {
          const ratio = capRatio(font)
          if (ratio > 0) cfg.cap_height_mm = cfg.font_size_mm * ratio
        }
      }
      cfg.letter_spacing = (cfg.letter_spacing || 0) * k
      if (cfg.stroke?.width) cfg.stroke.width = cfg.stroke.width * k
      if (cfg.inner_stroke?.width) cfg.inner_stroke.width = cfg.inner_stroke.width * k
      if (cfg.shadow) { cfg.shadow.dx = (cfg.shadow.dx || 0) * k; cfg.shadow.dy = (cfg.shadow.dy || 0) * k }
      n.scaleX(1); n.scaleY(1)
      this.replaceTextNode(n, cfg)
      if (this.onSelect) this.onSelect(this, this.selected)
    })

    this.gridGroup = this.buildGrid()
    this.gridGroup.visible(!!this.snap)
    this.layer.add(this.gridGroup)
    this.rulerGroup = this.buildRulers()
    this.rulerGroup.visible(!!this._rulerOn)
    this.layer.add(this.rulerGroup)
    this.marginGroup = this._buildMargin()
    this.marginGroup.visible(!!this._marginOn)
    this.layer.add(this.marginGroup)
    this.transformer.moveToTop()
  }

  // Перестроить под новый размер холста, сохранив слои.
  // Координаты слоёв относительные (мм) — независимы от размера, поэтому позиции точны.
  resize(width, height, scale) {
    const { s, tx, ty } = this.fit
    const snap = []
    let selIdx = -1, nodeIdx = 0
    this.clipGroup.getChildren().forEach((n) => {
      if (n === this.fillRect) {
        snap.push({ kind: "fill", color: n.fill() })
      } else if (this.isTextNode(n)) {
        if (n === this.selected) selIdx = nodeIdx
        snap.push({
          kind: "text",
          data: {
            center_x: (n.x() - tx) / s, center_y: (n.y() - ty) / s,
            rotation: n.rotation(), config: n.getAttr("config"), hidden: !n.visible(),
            link_id: n.getAttr("linkId"),
            group_id: n.getAttr("groupId"),
          },
        })
        nodeIdx++
      } else if (n.className === "Image") {
        const iw = n.image().width || 1, ih = n.image().height || 1
        if (n === this.selected) selIdx = nodeIdx
        snap.push({
          kind: "image", img: n.image(),
          data: {
            center_x: (n.x() - tx) / s, center_y: (n.y() - ty) / s,
            width: (n.width() * n.scaleX()) / s, height: (n.height() * n.scaleY()) / s,
            rotation: n.rotation(),
            crop_x: n.cropX() / iw, crop_y: n.cropY() / ih,
            crop_w: n.cropWidth() / iw, crop_h: n.cropHeight() / ih,
            hidden: !n.visible(),
            // атрибуты, не входящие в геометрию — иначе теряются при каждом resize
            image_signed_id: n.getAttr("signedId"),
            config: { name: n.getAttr("layerName") || "" },
            link_id: n.getAttr("linkId"),
            group_id: n.getAttr("groupId"),
          },
        })
        nodeIdx++
      }
    })

    this.select(null)
    this.clipGroup.destroy()
    this.contourGroup.destroy()
    this.transformer.destroy()
    this.gridGroup.destroy()
    this.rulerGroup.destroy()
    this.marginGroup.destroy()
    this.fillRect = null

    this.stage.size({ width, height })
    this._layoutFit(width, height, scale)
    this._buildScene()

    let ri = 0, toSelect = null
    for (const it of snap) {
      if (it.kind === "fill") { this.setFill(it.color); continue }
      const node = it.kind === "text" ? this.addExistingText(it.data) : this.addImageData(it.img, it.data)
      if (ri === selIdx) toSelect = node
      ri++
    }
    if (toSelect) this.select(toSelect)
    this.layer.draw()
    this.resetView()   // новый размер холста → вписанный вид, zoom=1
  }

  buildGrid() {
    const g = new Konva.Group({ listening: false, visible: false })
    const step = this.gridStep
    const x0 = ((this.gridOrigin.x % step) + step) % step
    const y0 = ((this.gridOrigin.y % step) + step) % step
    // индекс клетки считаем от нуля (центра детали), а не от края холста:
    // нулевые оси — самые толстые и тёмные, каждая 5-я (100 мм) — потолще
    const idx = (v, origin) => Math.round((v - origin) / step)
    const style = (n) => n === 0
      ? { stroke: "#1e40af", strokeWidth: 2.4, opacity: 0.9 }
      : (n % 5 === 0 ? { stroke: "#2563eb", strokeWidth: 1.4, opacity: 0.7 }
                     : { stroke: "#60a5fa", strokeWidth: 0.8, opacity: 0.7 })
    for (let x = x0; x <= this.width; x += step)
      g.add(new Konva.Line({ points: [x, 0, x, this.height], ...style(idx(x, this.gridOrigin.x)) }))
    for (let y = y0; y <= this.height; y += step)
      g.add(new Konva.Line({ points: [0, y, this.width, y], ...style(idx(y, this.gridOrigin.y)) }))
    return g
  }

  setGrid(on) {
    this.snap = on
    this.gridGroup.visible(on)
    this.layer.draw()
  }

  setGridMm(mm) {
    this.gridMm = mm
    this.gridStep = mm * 100 * this.fit.s
    this.gridGroup.destroy()
    this.gridGroup = this.buildGrid()
    this.gridGroup.visible(!!this.snap)
    this.layer.add(this.gridGroup)
    this.rulerGroup?.moveToTop()   // линейка остаётся поверх перестроенной сетки
    this.transformer.moveToTop()
    this.layer.draw()
  }

  // Линейки в мм: горизонтальная сверху, вертикальная слева. Нуль — в центре холста (как сетка).
  buildRulers() {
    const g = new Konva.Group({ listening: false, visible: false })
    // Линейка — фиксированная экранная шкала: рисуем в ЭКРАННЫХ координатах, а группу
    // контр-трансформируем, чтобы зум/пан стейджа её не тащили (иначе полоса раздувается и уезжает).
    const z = this.zoom || 1
    g.scale({ x: 1 / z, y: 1 / z })
    g.position({ x: -this.panX / z, y: -this.panY / z })
    const s = this.fit.s * z                       // экранный масштаб (px/unit) с учётом зума
    const ox = this.gridOrigin.x * z + this.panX   // экранный нуль (центр детали)
    const oy = this.gridOrigin.y * z + this.panY
    const band = 16                       // ширина полосы линейки, px
    const step = 10 * 100 * s             // риска каждые 10 мм
    // фон-подложки, чтобы риски читались поверх рисунка
    g.add(new Konva.Rect({ x: 0, y: 0, width: this.width, height: band, fill: "#ffffff", opacity: 0.85 }))
    g.add(new Konva.Rect({ x: 0, y: 0, width: band, height: this.height, fill: "#ffffff", opacity: 0.85 }))
    const big = (k) => k % 5 === 0        // каждые 50 мм — длиннее + подпись
    const x0 = ((ox % step) + step) % step
    for (let x = x0; x <= this.width; x += step) {
      if (x < band) continue
      const k = Math.round((x - ox) / step)
      const len = big(k) ? band : band * 0.5
      g.add(new Konva.Line({ points: [x, band - len, x, band], stroke: "#475569", strokeWidth: big(k) ? 1 : 0.6 }))
      if (big(k)) g.add(new Konva.Text({ x: x + 1, y: 1, text: `${Math.round((x - ox) / s / 100)}`, fontSize: 8, fill: "#475569" }))
    }
    const y0 = ((oy % step) + step) % step
    for (let y = y0; y <= this.height; y += step) {
      if (y < band) continue
      const k = Math.round((y - oy) / step)
      const len = big(k) ? band : band * 0.5
      g.add(new Konva.Line({ points: [band - len, y, band, y], stroke: "#475569", strokeWidth: big(k) ? 1 : 0.6 }))
      if (big(k)) g.add(new Konva.Text({ x: 1, y: y + 1, text: `${Math.round((y - oy) / s / 100)}`, fontSize: 8, fill: "#475569" }))
    }
    return g
  }

  // Перестроить линейку под текущий зум/пан (фиксированная экранная шкала).
  refreshRuler() {
    if (!this.rulerGroup) return
    this.rulerGroup.destroy()
    this.rulerGroup = this.buildRulers()
    this.rulerGroup.visible(this._rulerOn)
    this.layer.add(this.rulerGroup)
    this.transformer?.moveToTop()
  }

  setRuler(on) {
    this._rulerOn = on
    if (on) this.refreshRuler()
    else if (this.rulerGroup) this.rulerGroup.visible(false)
    this.layer.draw()
  }

  // Полоса-гайд отступа для кройки: контур детали широким полупрозрачным штрихом,
  // обрезанный по контуру → видна только внутренняя половина = безопасная зона.
  _buildMargin() {
    const { s, tx, ty } = this.fit
    const clip = new Path2D()
    clip.addPath(this.piece.path2d, new DOMMatrix([s, 0, 0, s, tx, ty]))
    const g = new Konva.Group({ clipFunc: () => [clip], listening: false })
    const inner = new Konva.Group({ x: tx, y: ty, scaleX: s, scaleY: s })
    const band = this.piece.isPolygon
      ? new Konva.Line({ points: this.piece.points, closed: true })
      : new Konva.Path({ data: this.piece.d })
    // Ширина полосы = припуск изделия (мм). strokeScaleEnabled(false) → штрих в экранных px,
    // поэтому под зум масштабируем вручную в _applyView (basePx × zoom).
    const marginMm = this.editorCtl?.seamAllowanceValue || 0
    this._marginBasePx = safeMarginBandPx(marginMm, s)
    band.stroke("#e11d48")
    band.strokeWidth(this._marginBasePx * (this.zoom || 1))
    band.strokeScaleEnabled(false)
    band.opacity(0.35)
    band.dash([6, 4])
    this.marginBand = band
    inner.add(band)
    g.add(inner)
    return g
  }

  setMargin(on) {
    this._marginOn = on
    if (this.marginGroup) this.marginGroup.visible(on)
    this.layer.draw()
  }

  // Konva-узлы картинок этой детали (без заливки и контура)
  imageNodes() {
    return this.clipGroup.getChildren().filter((n) => n.className === "Image")
  }

  // Текстовый слой — Konva.Group с attr("config"); отличаем от картинок/заливки.
  // У Konva.Group нет .className (только у фигур) — различаем по nodeType.
  isTextNode(n) {
    return n && n.nodeType === "Group" && !!n.getAttr("config")
  }

  // Персональный ли это слой — только такие можно править «под получателя».
  isPersonalNode(n) {
    return this.isTextNode(n) && !!n.getAttr("config")?.personalization?.enabled
  }

  // Режим получателя: правка касается ОДНОГО человека, поэтому всё, что принадлежит
  // общему макету (картинки, обычный текст, трансформер), запираем. Иначе оператор
  // «поправит фамилию Иванова» и незаметно сдвинет логотип у всей команды.
  setRecipientMode(on) {
    this._recipientMode = on
    for (const n of this.clipGroup.getChildren()) {
      if (n === this.fillRect) continue
      n.draggable(on ? this.isPersonalNode(n) : true)
    }
    if (on) {
      this.transformer.nodes([])
      if (this.selected && !this.isPersonalNode(this.selected)) this.select(null)
    } else if (this.selected) {
      this.transformer.nodes([ this.selected ])
    }
    this.layer.batchDraw()
  }

  // Общая привязка слоя к выделению/драгу (картинки и текст). Раньше инлайнилась в addLayer/addImageData.
  bindNode(node) {
    node.on("click tap", () => this.select(node))
    // Драг без предшествующего click (сразу потянули) не шлёт "click" → this.selected не
    // проставлялся: панель показывалась (через dragmove→onSelect), но кнопки масштаба/тулбара
    // (scaleSelected и пр. смотрят this.selected) молчали до повторного клика. Ставим выбор на старте драга.
    node.on("dragstart", () => {
      if (this.selected !== node) this.select(node)
      // в режиме получателя двигаем НЕ макет, а персональную правку: в историю не пишем
      // (Ctrl+Z макета не должен её откатывать) и запоминаем старт, чтобы вернуть узел
      if (this._recipientMode) { this._dragStart = node.position(); return }
      this.pushHistory()
    })
    node.on("dragmove", () => { if (this.onSelect) this.onSelect(this, node) })
    node.on("dragend", () => {
      if (this._recipientMode) { this._commitRecipientDrag(node); return }
      this.editorCtl?._dbgMeasure?.(this, node)   // DEBUG замер положения
      this.editorCtl?._syncGroup?.(this, node)
    })
  }

  // Перенести смещение с УЗЛА на внутреннюю группу и сохранить его как правку.
  // Узел возвращается ровно туда, где был, — визуально ничего не дёргается, а
  // exportLayers/_snapshot/resize (три места сериализации геометрии) продолжают видеть
  // неизменённый макет. Записать личную правку в общий макет попросту нечем.
  _commitRecipientDrag(node) {
    const start = this._dragStart
    this._dragStart = null
    if (!start) return
    const d = { x: node.x() - start.x, y: node.y() - start.y }
    node.position(start)
    const inner = node.findOne(".textInner")
    if (!inner) return
    inner.move(d)
    this.layer.batchDraw()
    const s = this.fit.s
    const mm = (px) => Math.round((px / (100 * s)) * 10) / 10   // до 0.1 мм — плоттеру точнее не нужно
    this.editorCtl?.saveOverride?.(node.getAttr("linkId"), this.piece.id, {
      dx_mm: mm(inner.x()), dy_mm: mm(inner.y()),
    })
  }

  addLayer(img) {
    this.pushHistory()
    const { s, tx, ty } = this.fit
    const b = this.piece.bbox
    let crop, w, h
    if (img._physicalMm) {
      // SVG с известным физ. размером — вставляем как есть (по width/height файла), без обрезки полей
      crop = { x: 0, y: 0, width: img.width, height: img.height }
      w = img._physicalMm.w * 100 * s
      h = img._physicalMm.h * 100 * s
    } else {
      crop = contentBounds(img)                     // обрезаем прозрачные поля → рамка по рисунку
      const dpi = img._rasterDpi || { x: DEFAULT_IMPORT_DPI, y: DEFAULT_IMPORT_DPI }
      w = physicalMmFromPx(crop.width, dpi.x) * 100 * s   // мм → units → px холста
      h = physicalMmFromPx(crop.height, dpi.y) * 100 * s
      if (!(w > 0) || !(h > 0)) {                   // крайний fallback: битый crop
        const target = DEFAULT_LAYER_MM * 100 * s
        const scale = target / (Math.max(crop.width, crop.height) || 1)
        w = crop.width * scale; h = crop.height * scale
      }
    }
    const node = new Konva.Image({
      image: img, crop,
      width: w, height: h, offsetX: w / 2, offsetY: h / 2,
      x: tx + (b.x + b.w / 2) * s,
      y: ty + (b.y + b.h / 2) * s,
      draggable: true,
      dragBoundFunc: this.dragBound,
    })
    // картинка уже залита отдельным запросом → на сейве уйдёт ссылкой, а не base64
    if (img._signedId) node.setAttr("signedId", img._signedId)
    this.bindNode(node)
    this.clipGroup.add(node)
    this.transformer.moveToTop()
    this.select(node)
    return node
  }

  // Восстановить слой-картинку из сохранённых данных (координаты SVG, units→px)
  addImageData(img, l) {
    const { s, tx, ty } = this.fit
    // отрицательная ширина = реверс (сохранён как знак scaleX)
    const flip = (l.width || 0) < 0
    const w = Math.abs(l.width) * s, h = Math.abs(l.height) * s
    // crop в долях натурального размера (важно для SVG). Старые макеты — в пикселях (>1).
    const iw = img.width || 1, ih = img.height || 1
    const frac = (l.crop_w ?? 0) <= 1 && (l.crop_h ?? 0) <= 1
    const crop = frac
      ? { x: l.crop_x * iw, y: l.crop_y * ih, width: l.crop_w * iw, height: l.crop_h * ih }
      : { x: l.crop_x, y: l.crop_y, width: l.crop_w, height: l.crop_h }
    const node = new Konva.Image({
      image: img,
      crop,
      width: w, height: h, offsetX: w / 2, offsetY: h / 2,
      scaleX: flip ? -1 : 1,
      x: tx + l.center_x * s, y: ty + l.center_y * s, rotation: l.rotation || 0,
      draggable: true,
      dragBoundFunc: this.dragBound,
    })
    // signed_id уже загруженного блоба → на ре-сейве шлём ссылку, а не base64 (см. exportLayers).
    // Для переноса на другую деталь signed_id берём с самого <img> (l его не несёт).
    if (l.image_signed_id || img._signedId) node.setAttr("signedId", l.image_signed_id || img._signedId)
    if (l.config?.name) node.setAttr("layerName", l.config.name)   // имя слоя из сохранённого макета
    if (l.link_id) node.setAttr("linkId", l.link_id)
    if (l.group_id) node.setAttr("groupId", l.group_id)
    if (l.hidden) node.visible(false)
    this.bindNode(node)
    this.clipGroup.add(node)
    this.transformer.moveToTop()
    this.layer.draw()
    return node
  }

  // Вставить картинку с сохранённой геометрией, центрировав на этой детали, и выделить
  addImageAt(img, geom, linkId) {
    this.pushHistory()
    const b = this.piece.bbox
    const node = this.addImageData(img, { ...geom, center_x: b.x + b.w / 2, center_y: b.y + b.h / 2, link_id: linkId })
    this.select(node)
  }

  // Заменить изображение существующего слоя-картинки: держим физическую ширину,
  // высоту по новому аспекту, crop сбрасываем, signedId снимаем (на сейве уйдёт новый blob).
  replaceImage(node, img, group = null) {
    this.pushHistory(null, group)
    const flip = node.scaleX() < 0
    const g = replacedImageGeom(node.width() * node.scaleX(), img)
    node.image(img)
    node.crop(g.crop)
    node.width(g.width); node.height(g.height)
    node.offsetX(g.width / 2); node.offsetY(g.height / 2)
    node.scaleX(flip ? -1 : 1); node.scaleY(1)
    // залитая отдельно картинка → ссылка; иначе exportLayers пошлёт image_data
    node.setAttr("signedId", img._signedId || null)
    this.transformer.forceUpdate()
    this.layer.batchDraw()
  }

  // --- Текстовый слой ---
  // Построить визуальный узел из config. Группа = (опц. тень) + основной путь.
  // Единицы config — в мм; геометрия (позиция узла) ставится снаружи (addText/addExistingText).
  // Если шрифт ещё не загружен — пустая группа с config (текст дорисуется при перестроении).
  // Структура узла: group (позиция якоря) → inner (scale k) → пути.
  // Масштаб автоподгонки живёт на ВНУТРЕННЕЙ группе, а не на самом узле: exportLayers
  // сериализует только позицию узла, поэтому автоподгонка (и позже — персональные
  // правки) физически не могут просочиться в геометрию слоя.
  buildTextNode(cfg, linkId = null) {
    const s = this.fit.s
    const group = new Konva.Group({ draggable: true, dragBoundFunc: this.dragBound })
    group.setAttr("config", cfg)
    if (linkId) group.setAttr("linkId", linkId)
    const fid = Number(cfg.font_id)
    const otf = this.editorCtl?.loadedFonts?.get(`${fid}:${styleKey(cfg)}`)
             || this.editorCtl?.loadedFonts?.get(`${fid}:regular`)
    if (!otf) return group
    const sizePx = (cfg.font_size_mm || 50) * 100 * s
    const spacingPx = (cfg.letter_spacing || 0) * 100 * s
    const text = resolveText(cfg, this.editorCtl?.previewRecipient)
    if (!text) return group
    const { pathD, width, baseline } = textToPath(otf, text, sizePx, spacingPx)
    const dx = cfg.align === "center" ? -width / 2 : cfg.align === "right" ? -width : 0

    // width здесь в px канваса, предел — в units; приводим предел к px тем же множителем
    const maxWpx = (cfg.max_width_mm || 0) * 100 * s
    // персональная правка активна только в режиме получателя
    const ov = this.editorCtl?.overrideFor?.(linkId, this.piece.id) || null
    const k = totalScale(width, maxWpx, cfg.font_size_mm, ov?.font_scale)
    const inner = new Konva.Group({
      name: "textInner", scaleX: k, scaleY: k,
      x: (ov?.dx_mm || 0) * 100 * s, y: (ov?.dy_mm || 0) * 100 * s,
    })
    group.add(inner)

    // baseline по метрикам шрифта (ascender/descender) → визуальный центр текста на якоре.
    // Тот же расчёт, что на сервере (coupon_svg.rb), → превью = финальный TIFF.
    if (cfg.shadow?.color) {
      inner.add(new Konva.Path({
        data: pathD, fill: cfg.shadow.color, listening: false,
        x: dx + (cfg.shadow.dx || 0) * 100 * s, y: baseline + (cfg.shadow.dy || 0) * 100 * s,
      }))
    }
    const main = new Konva.Path({ data: pathD, fill: cfg.color || "#000000", x: dx, y: baseline })
    if (cfg.stroke?.color && (cfg.stroke.width || 0) > 0) {
      main.stroke(cfg.stroke.color)
      main.strokeWidth(cfg.stroke.width * 100 * s)
      main.fillAfterStrokeEnabled(true)
      // round, как stroke-linejoin в купоне (coupon_svg.rb): дефолтный для Konva
      // miter выбрасывает на острых углах букв длинные шипы — и превью, и печать
      // должны показывать одно и то же
      main.lineJoin("round")
    }
    // Внутренняя обводка: тот же штрих, но ПОВЕРХ заливки и обрезанный по контуру глифа,
    // поэтому видна его внутренняя половина — ровно как у внешней видна наружная.
    // Одно и то же число в обоих полях даёт одинаковую видимую толщину с двух сторон.
    // Рисуем внутри sceneFunc основного пути, а не отдельным узлом: у узла без width/height
    // разъезжается clientRect группы, а за ним рамка трансформера.
    if (cfg.inner_stroke?.color && (cfg.inner_stroke.width || 0) > 0) {
      const insColor = cfg.inner_stroke.color
      const insWidth = cfg.inner_stroke.width * 100 * s
      const basePaint = main.sceneFunc()   // штатная отрисовка Konva.Path: заливка + внешняя обводка
      main.sceneFunc(function (ctx, shape) {
        basePaint.call(this, ctx, shape)
        const glyphs = new Path2D(pathD)
        ctx.save()
        ctx.clip(glyphs)
        ctx.setAttr("strokeStyle", insColor)
        ctx.setAttr("lineWidth", insWidth)
        ctx.setAttr("lineJoin", "round")   // как stroke-linejoin в купоне: miter даёт шипы на острых углах букв
        ctx.stroke(glyphs)
        ctx.restore()
      })
      // hit-канвас рисуется тем же sceneFunc, если не задан hitFunc, а на нём цвет —
      // это ключ узла: наш штрих закрасил бы часть буквы «ничьим» цветом и клик по ней
      // перестал бы попадать в слой. Поэтому для хитов — штатная отрисовка пути.
      main.hitFunc(function (ctx, shape) { basePaint.call(this, ctx, shape) })
    }
    inner.add(main)

    const frame = this._maxWidthFrame(cfg, width, maxWpx, sizePx)
    if (frame) group.add(frame)
    return group
  }

  // Пунктирная рамка предела ширины: показывает, куда обязана уместиться надпись.
  // Живёт в НЕмасштабированной группе — предел не должен ужиматься вместе с текстом.
  // Красная, если не влезло даже на минимально допустимом кегле.
  _maxWidthFrame(cfg, widthPx, maxWpx, sizePx) {
    if (maxWpx <= 0) return null
    const over = isOverflow(widthPx, maxWpx, cfg.font_size_mm)
    const x = cfg.align === "center" ? -maxWpx / 2 : cfg.align === "right" ? -maxWpx : 0
    return new Konva.Rect({
      name: "maxWidthFrame", listening: false, visible: false,
      x, y: -sizePx / 2, width: maxWpx, height: sizePx,
      stroke: over ? "#c33245" : "#8b5cf6", strokeWidth: 1, dash: [ 6, 4 ],
    })
  }

  // Рамка предела видна только у выделенного слоя — иначе холст превращается в кашу.
  // Узел может быть уже уничтожен (replaceTextNode) — тогда просто ничего не делаем.
  setFrameVisible(node, on) {
    if (!node || node.isDestroyed?.() || !node.getParent?.()) return
    node.findOne?.(".maxWidthFrame")?.visible(on)
  }

  // Новый текстовый слой по центру детали
  addText(cfg) {
    this.pushHistory("addText")
    const { s, tx, ty } = this.fit
    const b = this.piece.bbox
    const node = this.buildTextNode(cfg)
    node.position({ x: tx + (b.x + b.w / 2) * s, y: ty + (b.y + b.h / 2) * s })
    this.bindNode(node)
    this.clipGroup.add(node)
    this.transformer.moveToTop()
    this.select(node)
    this.layer.draw()
    return node
  }

  // Восстановить текстовый слой из сохранённого слоя l (координаты SVG units→px)
  addExistingText(l) {
    const { s, tx, ty } = this.fit
    // linkId прокидываем В buildTextNode, а не после: узел должен знать свой ключ уже
    // на момент рендера, чтобы подтянуть персональную правку
    const node = this.buildTextNode(l.config, l.link_id)
    node.position({ x: tx + (l.center_x || 0) * s, y: ty + (l.center_y || 0) * s })
    node.rotation(l.rotation || 0)
    if (l.hidden) node.visible(false)
    if (l.group_id) node.setAttr("groupId", l.group_id)
    this.bindNode(node)
    this.clipGroup.add(node)
    this.transformer.moveToTop()
    this.layer.draw()
    return node
  }

  // Перестроить узел text-слоя из обновлённого config, сохранив позицию/поворот/z и выделение
  replaceTextNode(node, cfg) {
    const pos = node.position()
    const rot = node.rotation()
    const z = node.zIndex()
    const wasSelected = node === this.selected
    const linkId = node.getAttr("linkId")
    const groupId = node.getAttr("groupId")
    node.destroy()
    const fresh = this.buildTextNode(cfg, linkId)
    fresh.position(pos)
    fresh.rotation(rot)
    if (groupId) fresh.setAttr("groupId", groupId)
    this.bindNode(fresh)
    this.clipGroup.add(fresh)
    fresh.zIndex(Math.min(z, this.clipGroup.getChildren().length - 1))
    this.transformer.moveToTop()
    if (wasSelected) this.select(fresh)
    this.layer.draw()
    return fresh
  }

  // Сериализация слоёв детали в координаты SVG (px→units)
  exportLayers() {
    const { s, tx, ty } = this.fit
    const out = []
    this.clipGroup.getChildren().forEach((node, i) => {
      if (node === this.fillRect) {
        out.push({ piece: this.piece.id, kind: "fill", z_index: i, color: node.fill() })
      } else if (this.isTextNode(node)) {
        out.push({
          piece: this.piece.id, kind: "text", z_index: i, hidden: !node.visible(),
          center_x: (node.x() - tx) / s, center_y: (node.y() - ty) / s,
          rotation: node.rotation(), config: node.getAttr("config"),
          link_id: this.linkIdOf(node),
          group_id: node.getAttr("groupId"),
        })
      } else if (node.className === "Image") {
        const signedId = node.getAttr("signedId")
        out.push({
          piece: this.piece.id, kind: "image", z_index: i, hidden: !node.visible(),
          center_x: (node.x() - tx) / s, center_y: (node.y() - ty) / s,
          width: (node.width() * node.scaleX()) / s, height: (node.height() * node.scaleY()) / s,
          rotation: node.rotation(),
          // crop в долях натурального размера картинки (resolution-independent, важно для SVG)
          crop_x: node.cropX() / (node.image().width || 1), crop_y: node.cropY() / (node.image().height || 1),
          crop_w: node.cropWidth() / (node.image().width || 1), crop_h: node.cropHeight() / (node.image().height || 1),
          config: { name: node.getAttr("layerName") || "" },   // имя слоя (панель/список)
          link_id: this.linkIdOf(node),
          group_id: node.getAttr("groupId"),
          // неизменённая картинка → ссылка на блоб (лёгкий payload); новая → base64
          ...(signedId ? { image_ref: signedId } : { image_data: node.image().src }),
        })
      }
    })
    return out
  }

  // Стабильный id слоя для связи между размерами. Читает атрибут linkId,
  // генерит и сохраняет на ноде, если пусто. Только image/text.
  linkIdOf(node) {
    let id = node.getAttr("linkId")
    if (!id) { id = crypto.randomUUID(); node.setAttr("linkId", id) }
    return id
  }

  // group_id создаётся ЯВНО (в отличие от linkIdOf) — только для слоёв в группе синхрона.
  ensureGroupId(node) {
    let id = node.getAttr("groupId")
    if (!id) { id = crypto.randomUUID(); node.setAttr("groupId", id) }
    return id
  }

  // Геометрия картинки в units/долях (без позиции) — для переноса на другую деталь
  serializeImage(node) {
    const { s } = this.fit
    const iw = node.image().width || 1, ih = node.image().height || 1
    return {
      width: (node.width() * node.scaleX()) / s,
      height: (node.height() * node.scaleY()) / s,
      rotation: node.rotation(),
      crop_x: node.cropX() / iw, crop_y: node.cropY() / ih,
      crop_w: node.cropWidth() / iw, crop_h: node.cropHeight() / ih,
    }
  }

  // Размер выбранной картинки в мм (целые, по модулю; реверс не влияет)
  sizeMm() {
    const n = this.selected
    if (!n || n.className !== "Image") return null
    const s = this.fit.s
    return {
      w: Math.round(Math.abs(n.width() * n.scaleX()) / s / 100),
      h: Math.round(Math.abs(n.height() * n.scaleY()) / s / 100),
    }
  }

  // Задать размер выбранной картинки по оси axis ("w"|"h") в мм. lock — вторую сторону пропорционально.
  applySizeMm(axis, mm, lock) {
    const n = this.selected
    if (!n || n.className !== "Image" || !(mm > 0)) return
    this.pushHistory("size")
    const s = this.fit.s
    const targetPx = mm * 100 * s
    const signX = n.scaleX() < 0 ? -1 : 1   // сохранить реверс
    if (axis === "w") {
      const sc = targetPx / n.width()
      n.scaleX(sc * signX)
      if (lock) n.scaleY(sc)
    } else {
      const sc = targetPx / n.height()
      n.scaleY(sc)
      if (lock) n.scaleX(sc * signX)
    }
    this.afterTransform()
  }

  // Смещение выбранного слоя от центра детали в мм ({x,y}) — для картинок и текста.
  // null, если нет выделения.
  offsetMm() {
    const n = this.selected
    if (!n) return null
    const { s, tx, ty } = this.fit
    const b = this.piece.bbox
    const cx = tx + (b.x + b.w / 2) * s
    const cy = ty + (b.y + b.h / 2) * s
    return { x: pxToMm(n.x() - cx, s), y: pxToMm(n.y() - cy, s) }
  }

  // Поставить выбранный слой по оси ("x"|"y") на смещение mm от центра детали.
  // Кламп центра слоя в границы холста (как в nudge).
  applyOffsetMm(axis, mm) {
    const n = this.selected
    if (!n || !Number.isFinite(mm)) return
    this.pushHistory("pos")
    const { s, tx, ty } = this.fit
    const b = this.piece.bbox
    if (axis === "x") {
      const cx = tx + (b.x + b.w / 2) * s
      n.x(clamp(cx + mmToPx(mm, s), 0, this.width))
    } else {
      const cy = ty + (b.y + b.h / 2) * s
      n.y(clamp(cy + mmToPx(mm, s), 0, this.height))
    }
    this.afterTransform()
  }

  // bbox детали в локальных px стейджа (контент для клампа пана)
  _contentBox() {
    const b = this.piece.bbox, { s, tx, ty } = this.fit
    return { x: tx + b.x * s, y: ty + b.y * s, w: b.w * s, h: b.h * s }
  }

  // Применить текущие zoom/pan к стейджу (+ обновить индикатор %)
  _applyView() {
    const { panX, panY } = clampPan({ zoom: this.zoom, panX: this.panX, panY: this.panY },
      { width: this.width, height: this.height }, this._contentBox())
    this.panX = panX; this.panY = panY
    this.stage.scale({ x: this.zoom, y: this.zoom })
    this.stage.position({ x: panX, y: panY })
    if (this.marginBand) this.marginBand.strokeWidth(this._marginBasePx * this.zoom)   // гайд припуска: экранные px × zoom
    if (this._rulerOn) this.refreshRuler()   // линейка — фиксированная шкала, переразметить под новый вид
    this.stage.batchDraw()
    if (this.zoomIndicator) this.zoomIndicator.textContent = `${Math.round(this.zoom * 100)}%`
  }

  // Зум к точке pointer (px канваса) множителем factor
  applyZoom(factor, pointer) {
    const nv = zoomToPoint({ zoom: this.zoom, panX: this.panX, panY: this.panY }, pointer, factor, { min: 1, max: 8 })
    this.zoom = nv.zoom; this.panX = nv.panX; this.panY = nv.panY
    this._applyView()
  }

  // Зум кнопкой — к центру холста
  zoomStep(factor) { this.applyZoom(factor, { x: this.width / 2, y: this.height / 2 }) }

  // Сброс к вписанному виду
  resetView() {
    this.zoom = 1; this.panX = 0; this.panY = 0
    this._applyView()
  }

  // Режим пана: стейдж таскается, драг слоёв подавлен, курсор «ладонь».
  setPanMode(on) {
    this._draggableSnapshot ||= new Map()
    const nodes = this.clipGroup ? this.clipGroup.getChildren((n) => n.draggable && n.draggable()) : []
    if (on) {
      nodes.forEach((n) => { this._draggableSnapshot.set(n, true); n.draggable(false) })
      this.stage.draggable(true)
      this.stage.dragBoundFunc((pos) => {
        const { panX, panY } = clampPan({ zoom: this.zoom, panX: pos.x, panY: pos.y },
          { width: this.width, height: this.height }, this._contentBox())
        return { x: panX, y: panY }
      })
      this.stage.container().style.cursor = "grab"
      // при пане синхронизируем pan и переразмечаем линейку вживую (она — фиксированная шкала)
      this.stage.on("dragmove.rulerpan", () => {
        this.panX = this.stage.x(); this.panY = this.stage.y()
        if (this._rulerOn) this.refreshRuler()
      })
    } else {
      this.stage.draggable(false)
      this.stage.dragBoundFunc(null)
      this.stage.off("dragmove.rulerpan")
      this._draggableSnapshot.forEach((_, n) => n.draggable(true))
      this._draggableSnapshot.clear()
      this.stage.container().style.cursor = ""
      this.panX = this.stage.x(); this.panY = this.stage.y()
    }
  }

  setKeepRatio(on) { this.transformer.keepRatio(on) }

  // Заливка детали сплошным цветом (базовый слой, всегда снизу, обрезан по контуру)
  setFill(color) {
    if (!this.fillRect) {
      this.fillRect = new Konva.Rect({ x: 0, y: 0, width: this.width, height: this.height, listening: false })
      this.clipGroup.add(this.fillRect)
      this.fillRect.moveToBottom()
    }
    this.fillRect.fill(color)
    this.layer.draw()
  }

  clearFill() {
    this.fillRect?.destroy()
    this.fillRect = null
    this.layer.draw()
  }

  select(node) {
    // рамку предела ширины показываем только у выделенного слоя
    this.setFrameVisible(this.selected, false)
    this.selected = node
    this.setFrameVisible(node, true)
    // в режиме получателя ручек трансформера нет: кегль/поворот — свойства общего макета,
    // персонально правим только смещение (драгом) и масштаб (полем в панели)
    this.transformer.nodes(node && !this._recipientMode ? [node] : [])
    if (this.onSelect) this.onSelect(this, node)
    this.layer.draw()
    this.renderLayers()
  }

  // Строки-слои детали (картинки/текст) сверху-вниз = перед-вверху. Заливка/контур — не в списке.
  _layerNodes() {
    return this.clipGroup
      ? this.clipGroup.getChildren((n) => n !== this.fillRect && (n.className === "Image" || this.isTextNode(n)))
      : []
  }

  _layerLabel(node) {
    const lid = node.getAttr("linkId")
    const idTag = lid ? `  ·  id:${String(lid).slice(0, 8)}` : "  ·  id:—"
    if (this.isTextNode(node)) {
      const t = (node.getAttr("config")?.content || "").trim()
      const base = t ? `Текст: ${t.length > 18 ? t.slice(0, 18) + "…" : t}` : "Текст"
      return base + idTag
    }
    return (node.getAttr("layerName") || "Картинка") + idTag
  }

  renderLayers() {
    if (!this.layerList) return
    const nodes = this._layerNodes().slice().reverse()   // верхний слой — первым
    if (this.layerCount) this.layerCount.textContent = `(${nodes.length})`
    this.layerList.innerHTML = ""
    // подсветка строки-цели DnD (индикатор места вставки — по хендоффу)
    const markOver = (el, on) => {
      el.classList.toggle("bg-[#f2f8fd]", on)
      el.classList.toggle("border-[#9fd0f5]", on)
      el.style.boxShadow = on ? "inset 0 2px 0 #128ddb" : ""
    }
    nodes.forEach((node, idx) => {
      const li = document.createElement("li")
      const selected = node === this.selected
      li.className = "flex cursor-pointer select-none items-center gap-2.5 rounded-[9px] border px-2 py-1.5 " +
        (selected ? "border-brand-blue-soft-border bg-brand-blue-soft" : "border-transparent hover:bg-gray-50")
      li.draggable = true
      li.addEventListener("dragstart", (e) => {
        e.dataTransfer.setData("text/plain", String(idx))
        e.dataTransfer.effectAllowed = "move"
        li.classList.add("opacity-40")
      })
      li.addEventListener("dragend", () => {
        li.classList.remove("opacity-40")
        for (const row of this.layerList.children) markOver(row, false)
      })
      li.addEventListener("dragover", (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; markOver(li, true) })
      li.addEventListener("dragleave", () => markOver(li, false))
      li.addEventListener("drop", (e) => {
        e.preventDefault()
        markOver(li, false)
        const from = parseInt(e.dataTransfer.getData("text/plain"), 10)
        if (Number.isNaN(from) || from === idx) return
        this.applyLayerOrder(moveItem(nodes, from, idx))
      })

      // грип — аффорданс перетаскивания (6 точек)
      const grip = document.createElement("span")
      grip.className = "shrink-0 cursor-grab text-[#c0cad6]"
      grip.innerHTML = '<svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>'
      li.appendChild(grip)

      // мини-превью 34×34: картинка — миниатюра, текст — первые символы своим цветом на шахматке
      const pv = document.createElement("div")
      pv.className = "flex h-[34px] w-[34px] shrink-0 items-center justify-center overflow-hidden rounded-[7px] border border-line bg-[repeating-conic-gradient(#e4e9ef_0_25%,#fff_0_50%)] bg-[length:8px_8px]"
      if (this.isTextNode(node)) {
        const cfg = node.getAttr("config") || {}
        const chars = document.createElement("span")
        chars.className = "text-[13px] font-extrabold leading-none"
        chars.style.color = cfg.color || "#000"
        chars.style.webkitTextStroke = "0.4px rgba(0,0,0,.25)"
        const src = (cfg.personalization?.enabled ? cfg.personalization.sample : cfg.content) || "Aa"
        chars.textContent = src.trim().slice(0, 3)
        pv.appendChild(chars)
      } else if (node.className === "Image" && node.image()?.src) {
        const thumb = document.createElement("img")
        thumb.src = node.image().src
        thumb.className = "h-full w-full object-contain"
        pv.appendChild(thumb)
      }
      li.appendChild(pv)

      const label = document.createElement("span")
      label.className = "min-w-0 flex-1 truncate text-[13px]" + (node.visible() ? "" : " text-gray-400 line-through")
      label.textContent = this._layerLabel(node)
      li.appendChild(label)

      const type = document.createElement("span")
      type.className = "shrink-0 text-[11px] text-brand-muted-2"
      type.textContent = this.isTextNode(node) ? "Текст" : "Картинка"
      li.appendChild(type)

      const eye = document.createElement("button")
      eye.type = "button"
      eye.title = node.visible() ? "Скрыть слой" : "Показать слой"
      eye.className = "shrink-0 rounded p-0.5 text-brand-graphite hover:bg-gray-100"
      eye.textContent = node.visible() ? "👁" : "🚫"
      eye.addEventListener("click", (e) => {
        e.stopPropagation()   // не выделять слой при клике по глазу
        node.visible(!node.visible())
        this.layer.draw()
        this.renderLayers()
      })
      li.appendChild(eye)
      li.addEventListener("click", () => this.select(node))
      this.layerList.appendChild(li)
    })
  }

  // Применить новый порядок узлов (сверху-вниз) к clipGroup. Заливка остаётся на дне.
  applyLayerOrder(topFirstNodes) {
    this.pushHistory()
    const bottomFirst = topFirstNodes.slice().reverse()
    let z = 0
    if (this.fillRect) this.fillRect.zIndex(z++)
    bottomFirst.forEach((n) => n.zIndex(z++))
    this.transformer.moveToTop()
    this.layer.draw()
    this.renderLayers()
  }

  // --- Кнопочные трансформации выбранного слоя (вокруг центра) ---
  scaleSelected(f) {
    if (!this.selected) return
    this.pushHistory("scale")
    this.selected.scaleX(this.selected.scaleX() * f)
    this.selected.scaleY(this.selected.scaleY() * f)
    this.afterTransform()
  }

  // Абсолютный поворот из поля «Поворот, °» в панели свойств
  setRotationSelected(deg) {
    if (!this.selected) return
    this.pushHistory("rotate")
    this.selected.rotation(deg)
    this.afterTransform()
  }

  // Сдвиг выбранного слоя стрелками.
  // Сетка выкл: точный шаг в мм (dxMm/dyMm, 100 units = 1 мм).
  // Сетка вкл: прыжок на `cells` ячеек сетки в сторону стрелки (как drag со snap).
  nudge(dxMm, dyMm, cells = 1) {
    if (!this.selected) return
    this.pushHistory("nudge")
    let x, y
    if (this.snap) {
      x = dxMm !== 0
        ? snapStep(this.selected.x(), this.gridOrigin.x, this.gridStep, Math.sign(dxMm), cells)
        : this.selected.x()
      y = dyMm !== 0
        ? snapStep(this.selected.y(), this.gridOrigin.y, this.gridStep, Math.sign(dyMm), cells)
        : this.selected.y()
    } else {
      const k = 100 * this.fit.s   // мм → px
      x = this.selected.x() + dxMm * k
      y = this.selected.y() + dyMm * k
    }
    x = Math.max(0, Math.min(this.width, x))
    y = Math.max(0, Math.min(this.height, y))
    this.selected.position({ x, y })
    this.afterTransform()
  }

  // Реверс (зеркало по горизонтали) — отрицательный scaleX
  flipSelected() {
    if (!this.selected) return
    this.pushHistory()
    this.selected.scaleX(this.selected.scaleX() * -1)
    this.afterTransform()
  }

  // Вписать слой во всю деталь (cover) — удобно для бекграунда
  coverSelected() {
    if (!this.selected) return
    this.pushHistory()
    const n = this.selected
    const { s, tx, ty } = this.fit
    const b = this.piece.bbox
    const sc = Math.max((b.w * s) / n.width(), (b.h * s) / n.height())
    n.scaleX(sc); n.scaleY(sc); n.rotation(0)
    n.position({ x: tx + (b.x + b.w / 2) * s, y: ty + (b.y + b.h / 2) * s })
    this.afterTransform()
  }

  // Вернуть выбранный слой в центр детали (восстановление, если уехал за контур)
  recenterSelected() {
    if (!this.selected) return
    this.pushHistory()
    const { s, tx, ty } = this.fit
    const b = this.piece.bbox
    this.selected.position({ x: tx + (b.x + b.w / 2) * s, y: ty + (b.y + b.h / 2) * s })
    this.afterTransform()
  }

  afterTransform() {
    this.transformer.forceUpdate()
    this.layer.draw()
    if (this.selected) this.editorCtl?._syncGroup?.(this, this.selected)
  }

  deleteSelected() {
    if (!this.selected) return
    this.pushHistory()
    this.selected.destroy()
    this.select(null)
  }

  // --- Undo на уровне детали ---
  // Снимок состояния детали (заливка + картинки) с живыми ссылками на img,
  // в units/долях — та же кодировка, что в resize()/exportLayers(), round-trip точный.
  _snapshot() {
    const { s, tx, ty } = this.fit
    const out = []
    this.clipGroup.getChildren().forEach((n) => {
      if (n === this.fillRect) {
        out.push({ kind: "fill", color: n.fill() })
      } else if (this.isTextNode(n)) {
        out.push({
          kind: "text",
          data: {
            center_x: (n.x() - tx) / s, center_y: (n.y() - ty) / s,
            rotation: n.rotation(), config: n.getAttr("config"), hidden: !n.visible(),
            link_id: n.getAttr("linkId"),
            group_id: n.getAttr("groupId"),
          },
        })
      } else if (n.className === "Image") {
        const iw = n.image().width || 1, ih = n.image().height || 1
        out.push({
          kind: "image", img: n.image(),
          data: {
            center_x: (n.x() - tx) / s, center_y: (n.y() - ty) / s,
            width: (n.width() * n.scaleX()) / s, height: (n.height() * n.scaleY()) / s,
            rotation: n.rotation(),
            crop_x: n.cropX() / iw, crop_y: n.cropY() / ih,
            crop_w: n.cropWidth() / iw, crop_h: n.cropHeight() / ih,
            hidden: !n.visible(),
            link_id: n.getAttr("linkId"),
            group_id: n.getAttr("groupId"),
          },
        })
      }
    })
    return out
  }

  // Зафиксировать состояние ДО изменения. coalesce — склейка серии (нарезка стрелками,
  // ползунок размера, клики масштаба/поворота) в один шаг отмены, если идёт подряд за <700мс.
  pushHistory(coalesce = null, group = null) {
    const now = Date.now()
    if (coalesce && this._histKey === coalesce && now - this._histTime < 700) {
      this._histTime = now
      return
    }
    const snap = this._snapshot()
    snap.group = group
    this.history.push(snap)
    if (this.history.length > this.HISTORY_LIMIT) this.history.shift()
    this._histKey = coalesce
    this._histTime = now
    if (this.onHistory) this.onHistory(this)
  }

  canUndo() { return this.history.length > 0 }

  // group-токен верхнего снимка (для связанной групповой отмены across деталей)
  peekGroup() { const h = this.history; return h.length ? (h[h.length - 1].group ?? null) : null }

  undo() {
    const snap = this.history.pop()
    if (!snap) return false
    this._histKey = null
    this.select(null)
    this.clipGroup.destroyChildren()
    this.fillRect = null
    for (const it of snap) {
      if (it.kind === "fill") this.setFill(it.color)
      else if (it.kind === "text") this.addExistingText(it.data)
      else this.addImageData(it.img, it.data)
    }
    this.transformer.moveToTop()
    this.layer.draw()
    if (this.onHistory) this.onHistory(this)
    return true
  }

  reset() {
    this.clipGroup.destroyChildren()
    this.fillRect = null
    this.select(null)
  }

  destroy() { this.stage.destroy() }
}

// --- helpers ---
function snapTo(v, origin, step) {
  return origin + Math.round((v - origin) / step) * step
}

// Прыжок к следующей линии сетки в сторону dir (±1), затем +(cells-1) полных ячеек.
// Если деталь между линиями — первый шаг выравнивает на ближайшую линию в нужную сторону.
function snapStep(v, origin, step, dir, cells) {
  const line = origin + Math.round((v - origin) / step) * step  // ближайшая линия
  const eps = step * 1e-6
  let base
  if (dir > 0) base = v < line - eps ? line : line + step
  else         base = v > line + eps ? line : line - step
  return base + dir * (cells - 1) * step
}

function csrfToken() {
  return document.querySelector("meta[name=csrf-token]")?.content || ""
}

// Загрузить картинку из URL → HTMLImageElement (как dataURL, чтобы ре-сейв слал base64).
// Active Storage отдаёт SVG как application/octet-stream — определяем тип по байтам.
async function loadImage(url) {
  const r = await fetch(url)
  if (!r.ok) throw new Error(`HTTP ${r.status} для ${url}`)
  const blob = await r.blob()
  let type = blob.type
  if (!type || type === "application/octet-stream") {
    const head = await blob.slice(0, 256).text()
    type = /<svg|<\?xml/i.test(head) ? "image/svg+xml" : "image/png"
  }
  const dataUrl = await new Promise((res) => {
    const reader = new FileReader()
    reader.onload = () => res(reader.result)
    reader.readAsDataURL(new Blob([blob], { type }))
  })
  return new Promise((res, rej) => {
    const img = new window.Image()
    img.onload = () => res(img)
    img.onerror = () => rej(new Error(`decode failed (${type})`))
    img.src = dataUrl
  })
}

function readImage(file, cb) {
  if (!file) { cb(null); return }
  // transform(img, done) вызывается перед cb — навешивает _physicalMm (SVG)
  // или проставляет DPI и пересэмплит растр под печатное разрешение
  const loadImg = (transform) => {
    const reader = new FileReader()
    reader.onerror = () => cb(null)
    reader.onload = () => {
      const img = new window.Image()
      img.onload = () => (transform ? transform(img, cb) : cb(img))
      img.onerror = () => cb(null)
      img.src = reader.result
    }
    reader.readAsDataURL(file)
  }
  const isSvg = file.type === "image/svg+xml" || /\.svg$/i.test(file.name || "")
  if (isSvg) {
    const tr = new FileReader()
    tr.onerror = () => loadImg(null)
    tr.onload = () => {
      const mm = svgPhysicalSizeMm(tr.result)
      // вектор не пересэмплим — он масштабируется без потерь
      loadImg((img, done) => {
        if (mm) img._physicalMm = mm
        img._uploadBlob = file; img._uploadName = file.name
        done(img)
      })
    }
    tr.readAsText(file)
    return
  }
  // растр: читаем байты для вшитого DPI
  const br = new FileReader()
  br.onerror = () => loadImg(null)
  br.onload = () => {
    const dpi = rasterDpi(br.result)
    loadImg((img, done) => {
      if (dpi) img._rasterDpi = dpi
      img._uploadBlob = file; img._uploadName = file.name   // пересэмплинг подменит на ужатый вариант
      resampleForPrint(img, dpi, file.type, done)
    })
  }
  br.readAsArrayBuffer(file)
}

// SHA-256 содержимого блоба (hex) — ключ кеша уже залитых картинок.
// crypto.subtle есть только в secure context (https/localhost); иначе null и
// кеш просто не работает — заливка пройдёт как обычно.
async function blobHash(blob) {
  try {
    if (!crypto?.subtle) return null
    const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer())
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("")
  } catch (e) {
    return null
  }
}

// data:URL → Blob для отдельной заливки на сервер (без повторного кодирования
// картинки: байты уже посчитаны в toDataURL). null, если строка не разобралась.
function dataUrlToBlob(dataUrl) {
  try {
    const [meta, b64] = dataUrl.split(",", 2)
    const type = meta.match(/data:(.*?);/)?.[1] || "image/png"
    const bin = atob(b64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    return new Blob([bytes], { type })
  } catch (e) {
    return null
  }
}

// Пересэмплить растр под печатные 150 DPI, сохранив физический размер слоя.
// Отдаёт новый HTMLImageElement с _rasterDpi = PRINT_DPI (чтобы addLayer посчитал
// тот же размер в мм) или исходный, если ужимать нечего либо canvas не осилил.
function resampleForPrint(img, dpi, type, cb) {
  const scale = resampleScale(dpi?.x || DEFAULT_IMPORT_DPI)
  if (scale === 1) { cb(img); return }
  const { width, height } = resampledSize(img.width, img.height, scale)
  console.log("[editor] пересэмплинг под печать", { from: `${img.width}×${img.height}`, to: `${width}×${height}` })
  try {
    const c = document.createElement("canvas")
    c.width = width; c.height = height
    const ctx = c.getContext("2d")
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(img, 0, 0, width, height)
    // JPEG без альфы остаётся JPEG (вчетверо легче PNG), всё остальное — PNG с прозрачностью
    const mime = type === "image/jpeg" ? "image/jpeg" : "image/png"
    const dataUrl = c.toDataURL(mime, 0.92)
    const out = new window.Image()
    out.onload = () => {
      out._rasterDpi = { x: PRINT_DPI, y: PRINT_DPI }
      // на сервер уходит ужатый вариант, а не оригинал
      out._uploadBlob = dataUrlToBlob(dataUrl) || img._uploadBlob
      out._uploadName = img._uploadName
      cb(out)
    }
    out.onerror = () => cb(img)
    out.src = dataUrl
  } catch (e) {
    console.warn("[editor] пересэмплинг не удался, берём оригинал", e)
    cb(img)
  }
}

// bbox непрозрачного содержимого картинки (обрезка прозрачных полей)
function contentBounds(img) {
  const c = document.createElement("canvas")
  c.width = img.width; c.height = img.height
  const ctx = c.getContext("2d")
  ctx.drawImage(img, 0, 0)
  let data
  try {
    data = ctx.getImageData(0, 0, c.width, c.height).data
  } catch (e) {
    return { x: 0, y: 0, width: img.width, height: img.height }
  }
  const A = 10
  let minX = img.width, minY = img.height, maxX = -1, maxY = -1
  for (let y = 0; y < img.height; y++) {
    for (let x = 0; x < img.width; x++) {
      if (data[(y * img.width + x) * 4 + 3] > A) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }
  if (maxX < minX) return { x: 0, y: 0, width: img.width, height: img.height }  // пусто/непрозрачно
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 }
}

function toPath2D(el) {
  const pts = shapePoints(el)
  return new Path2D(pts ? pointsToPathD(pts) : el.getAttribute("d"))
}
